import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import HistoryIcon from '@mui/icons-material/History';
import HomeIcon from '@mui/icons-material/Home';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlaceIcon from '@mui/icons-material/Place';
import PublicIcon from '@mui/icons-material/Public';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SaveIcon from '@mui/icons-material/Save';
import ShieldIcon from '@mui/icons-material/Shield';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { supabase } from '../config/supabase';
import { useAuditUserId, useAuth } from '../auth/AuthContext';
import { useDraftPersistence } from '../hooks/useDraftPersistence';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';
import { lookupLabel } from '../data/lookups';
import { lineupStatusOptions, matchClassificationOptions, matchSiteTypeOptions, matchStatusOptions, opponentRatingBasisOptions, opponentSubjectiveDifficultyOptions } from '../data/options';
import type { LookupState, MatchEventRecord, MatchSiteType, MatchStatus, OpponentRatingBasis, SortDirection, TimeFormat, UserPreferences } from '../types';
import { formatPlayerFirstLast, formatPlayerName } from '../utils/playerPreferences';
import { lookupTeamAccentColor } from '../utils/teamColors';
import { formatTime, toTimeInput } from '../utils/timeFormat';
import { groupMeReminderOptions, groupMeReminderSummary, normalizeGroupMeReminders } from '../utils/groupmeReminders';
import { formatEdr } from '../utils/edr';
import { MatchParticipationEvents } from './MatchParticipationEvents';
import { MatchResults } from './MatchResults';
import { SavedMatchResultSummary } from './SavedMatchResultSummary';
import { CalendarPublicationPanel } from './CalendarPublicationPanel';
import { SeasonTeamMultiSelect } from './SeasonTeamMultiSelect';
import { DraftRecoveryDialog } from './DraftRecoveryDialog';
import { DraftSavedIndicator } from './DraftSavedIndicator';
import { BulkMatchImport } from './BulkMatchImport';
import { BreadcrumbTrail } from './ux/BreadcrumbTrail';
import { ContextBackButton } from './ux/ContextBackButton';
import { buildBreadcrumbTrail, matchCanonicalContext, resolveContextBackDestination } from '../utils/uxNavigation';

type MatchSchedulingProps = {
  lookups: LookupState;
  initialMatchId?: string | null;
  initialScoreEditorOpen?: boolean;
  initialEditMatchId?: string | null;
  initialCreateMatchOpen?: boolean;
  initialTeamFilterId?: string | null;
  mode?: 'list' | 'detail';
  onMatchOpened?: () => void;
  onEditMatchOpened?: () => void;
  onCreateMatchOpened?: () => void;
  onOpenMatchDetail?: (matchId: string) => void;
  onEditMatchRoute?: (matchId: string) => void;
  onTeamFilterApplied?: () => void;
  onOpenLineup?: (matchId: string) => void;
  onOpenAvailability?: (teamId?: string | null, matchId?: string | null) => void;
  onBackToMatches?: () => void;
  onOpenTeamEvents?: () => void;
  backLabel?: string;
  breadcrumbOrigin?: 'team-events' | 'planning-board';
  isWorkflowFullScreen?: boolean;
  onExitWorkflowFullScreen?: () => void;
  onEditMatchSaved?: () => void;
  onCreateMatchSaved?: () => void;
  onCreateMatchCancelled?: () => void;
  onScoreEditorClosed?: () => void;
  timeFormat: TimeFormat;
  userPreferences: UserPreferences;
  readOnly?: boolean;
  canSendEmail?: boolean;
};

type TeamDetail = {
  id: string;
  team_name: string;
  team_color: string | null;
  organization_id: string;
  season_id: string;
  league_configuration_id: string;
  home_facility_id: string | null;
  league_name: string | null;
  home_match_opponent_message_template: string | null;
  default_warmup_minutes: number | null;
  default_groupme_event_reminders?: number[] | null;
};

type MatchForm = {
  team_id: string;
  season_id: string;
  league_configuration_id: string;
  match_classification: string;
  match_site_type: MatchSiteType;
  match_date: string;
  match_time: string;
  warmup_time: string;
  opponent_name: string;
  opponent_average_edr: string;
  opponent_tennis_record_roster_url: string;
  opponent_edr_overall: string;
  opponent_edr_source: 'manual' | 'tennis_record';
  opponent_rating_basis: OpponentRatingBasis;
  opponent_subjective_difficulty: string;
  opponent_captain_names: string;
  opponent_captain_email: string;
  opponent_captain_phone: string;
  opponent_co_captain_name: string;
  opponent_co_captain_email: string;
  opponent_co_captain_phone: string;
  facility_id: string;
  court_numbers: string;
  groupme_event_title: string;
  groupme_event_description: string;
  groupme_event_image_url: string;
  groupme_event_time_source: 'match_time' | 'warmup_time';
  groupme_event_end_time: string;
  groupme_reminder_mode: 'team_default' | 'custom';
  groupme_custom_reminders: number[];
  match_status: MatchStatus;
  notes_summary: string;
};

type Snack = {
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
};

type FacilityMessageDetail = {
  facility_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  google_maps_url: string | null;
  warmup_booking_url: string | null;
};

type CaptainAssignmentMessageRow = {
  app_user_id: string;
  assignment_role: string;
  is_primary_captain: boolean;
  created_at?: string | null;
};

type AppUserMessageRow = {
  id: string;
  display_name: string | null;
};

type MatchSortField = 'match_date' | 'team_id' | 'match_status' | 'lineup_status' | 'health';
type DateRangePreset = 'upcoming' | 'this_week' | 'next_30_days' | 'this_season' | 'past' | 'all' | 'custom';
type MatchSchedulingViewState = {
  teamFilters: string[];
  matchStatusFilters: MatchStatus[];
  dateRangePreset: DateRangePreset;
  dateFromFilter: string;
  dateToFilter: string;
  matchSort: {
    field: MatchSortField;
    direction: SortDirection;
  };
};
type RecommendationFreshness = {
  status: 'Fresh' | 'Needs Refresh' | 'Never Generated' | 'N/A';
  detail: string;
};

type RecommendationSummaryRow = {
  affected_match_ids: string[] | null;
  generated_at: string | null;
  updated_at: string | null;
};

type MatchBulkField =
  | 'match_date'
  | 'opponent_name'
  | 'match_site_type'
  | 'match_time'
  | 'warmup_time'
  | 'facility_id'
  | 'court_numbers'
  | 'match_status'
  | 'match_classification'
  | 'opponent_captain_names'
  | 'opponent_captain_email'
  | 'opponent_captain_phone'
  | 'opponent_co_captain_name'
  | 'opponent_co_captain_email'
  | 'opponent_co_captain_phone'
  | 'opponent_tennis_record_roster_url'
  | 'notes_summary';

type MatchBulkDraft = Partial<Record<MatchBulkField, string>>;

type LineupCopyPosition = {
  id: string;
  position_code: string;
  display_order: number;
};

type LineupCopyEntry = {
  match_event_id: string;
  league_configuration_position_id: string;
  slot_number: number;
  player_id: string;
};

type LineupCopyPlayer = {
  id: string;
  first_name: string | null;
  last_name: string | null;
};

type LineupCopyStatus = {
  league_configuration_position_id: string;
  line_status: 'normal' | 'default_by_opponent' | 'default_by_us';
};

type LineupCopyStandby = {
  player_id: string;
  notes: string | null;
};

type MatchLifecycleHistoryRow = {
  id: string;
  previous_lifecycle_state: string | null;
  new_lifecycle_state: string;
  changed_at: string;
  changed_by: string | null;
  change_reason: string | null;
};

type AvailabilitySummary = {
  total: number;
  known: number;
  available: number;
};

type MatchResultSummary = {
  match_event_id: string;
  our_team_score: number | null;
  opponent_team_score: number | null;
  team_result: string | null;
  team_result_value: string | null;
  calculated_result_snapshot: { explanation?: string; lineWins?: { Us?: number; Opponent?: number } } | null;
  result_status: string | null;
  updated_at: string | null;
  updated_by: string | null;
};

type OpposingCaptainEmailDraft = {
  from: string;
  to: string;
  subject: string;
  body: string;
  missing: string[];
};

type OpposingCaptainEmailSend = {
  id: string;
  gmail_email: string;
  recipient_email: string;
  subject: string;
  sent_at: string;
  send_status: string;
};

const emptyMatchForm: MatchForm = {
  team_id: '',
  season_id: '',
  league_configuration_id: '',
  match_classification: 'regular_season',
  match_site_type: 'Unknown',
  match_date: '',
  match_time: '',
  warmup_time: '',
  opponent_name: '',
  opponent_average_edr: '',
  opponent_tennis_record_roster_url: '',
  opponent_edr_overall: '',
  opponent_edr_source: 'manual',
  opponent_rating_basis: 'avg_edr',
  opponent_subjective_difficulty: '3',
  opponent_captain_names: '',
  opponent_captain_email: '',
  opponent_captain_phone: '',
  opponent_co_captain_name: '',
  opponent_co_captain_email: '',
  opponent_co_captain_phone: '',
  facility_id: '',
  court_numbers: '',
  groupme_event_title: '',
  groupme_event_description: '',
  groupme_event_image_url: '',
  groupme_event_time_source: 'match_time',
  groupme_event_end_time: '',
  groupme_reminder_mode: 'team_default',
  groupme_custom_reminders: [],
  match_status: 'Draft',
  notes_summary: '',
};

const defaultOpponentCaptainTemplate = `This is {CaptainNames} from the {TeamName} team of the {LeagueName}. I'm confirming our match time of {MatchTime} on {MatchDate} at {FacilityName} ({GoogleMapsLink}).

I'll confirm the court numbers in the next few days and send them. Your team can warm up on the match courts starting {WarmupMinutes} minutes prior to match time. If you want additional warm-up court time you can use the Impact Activities App to book it. ({WarmupBookingLink}).

Water and snacks are available in the pro shop but not on the courts themselves. Please let me know if you have any questions about the facility. Looking forward to it!`;

function subtractMinutes(time: string, minutesToSubtract: number): string {
  const [hoursRaw, minutesRaw] = time.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '';
  const total = (hours * 60 + minutes - minutesToSubtract + 24 * 60) % (24 * 60);
  const warmupHours = Math.floor(total / 60);
  const warmupMinutes = total % 60;
  return `${String(warmupHours).padStart(2, '0')}:${String(warmupMinutes).padStart(2, '0')}`;
}

function formatMatchDate(value: string | null | undefined): string {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function formatShortDate(value: string | null | undefined): string {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function dateInputValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function startOfWeek(date: Date): Date {
  const next = new Date(date);
  next.setDate(next.getDate() - next.getDay());
  return next;
}

function dateRangeBounds(preset: DateRangePreset, customFrom: string, customTo: string): { from: string; to: string } {
  const today = new Date();
  const todayValue = dateInputValue(today);
  if (preset === 'upcoming') return { from: todayValue, to: '' };
  if (preset === 'this_week') return { from: dateInputValue(startOfWeek(today)), to: dateInputValue(addDays(startOfWeek(today), 6)) };
  if (preset === 'next_30_days') return { from: todayValue, to: dateInputValue(addDays(today, 30)) };
  if (preset === 'past') return { from: '', to: dateInputValue(addDays(today, -1)) };
  if (preset === 'custom') return { from: customFrom, to: customTo };
  return { from: '', to: '' };
}

function formatDateTimeParts(value: string | null | undefined): { date: string; time: string } | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return {
    date: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
    time: date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
  };
}

function listLabel(value: string | null | undefined, singular: string, plural: string): string {
  const count = (value || '').split(',').map((part) => part.trim()).filter(Boolean).length;
  return count > 1 ? plural : singular;
}

function opponentDifficultyLabel(value: number | null | undefined): string {
  return opponentSubjectiveDifficultyOptions.find((option) => option.value === String(value))?.label || 'Even';
}

function userTimelineLabel(label: string): string {
  return label.replace(/\s*\([^)]*@[^)]*\)\s*$/, '').trim();
}

function matchAccentColor(matchStatus: MatchStatus | null | undefined): string {
  if (matchStatus === 'Completed') return '#1976d2';
  if (matchStatus === 'Cancelled') return '#d32f2f';
  if (matchStatus === 'Postponed' || matchStatus === 'Rescheduled') return '#ed6c02';
  return '#2e7d32';
}

function matchStatusColor(matchStatus: MatchStatus | null | undefined): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' {
  if (matchStatus === 'Completed') return 'primary';
  if (matchStatus === 'Scores Entered') return 'info';
  if (matchStatus === 'Cancelled') return 'error';
  if (matchStatus === 'Postponed' || matchStatus === 'Rescheduled') return 'warning';
  return 'success';
}

function lineupStatusColor(lineupStatus: string | null | undefined): 'default' | 'primary' | 'success' | 'warning' | 'info' {
  if (lineupStatus === 'Published') return 'success';
  if (lineupStatus === 'Draft') return 'warning';
  return 'default';
}

function warmupMinutes(matchTime: string | null | undefined, warmupTime: string | null | undefined): string {
  const match = toTimeInput(matchTime || null);
  const warmup = toTimeInput(warmupTime || null);
  if (!match || !warmup) return '';
  const [matchHours, matchMinutes] = match.split(':').map(Number);
  const [warmupHours, warmupMins] = warmup.split(':').map(Number);
  if ([matchHours, matchMinutes, warmupHours, warmupMins].some((value) => !Number.isFinite(value))) return '';
  const matchTotal = matchHours * 60 + matchMinutes;
  const warmupTotal = warmupHours * 60 + warmupMins;
  const diff = matchTotal - warmupTotal;
  return diff >= 0 ? String(diff) : '';
}

function addMinutesToTimeInput(time: string | null | undefined, minutes: number): string {
  const normalized = toTimeInput(time || null);
  if (!normalized || !Number.isFinite(minutes)) return '';
  const [hours, mins] = normalized.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return '';
  const total = (((hours * 60 + mins + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function facilityAddress(facility: FacilityMessageDetail | null): string {
  if (!facility) return '';
  const cityStateZip = [facility.city, [facility.state, facility.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [facility.address_line1, facility.address_line2, cityStateZip].filter(Boolean).join(', ');
}

function applyTemplate(template: string, tokens: Record<string, string>): string {
  return template.replace(/\{([A-Za-z]+)\}/g, (_match, token: string) => tokens[token] ?? '');
}

function formatNameList(names: string[]): string {
  if (names.length <= 1) return names[0] || '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function optionLabel(options: { value: string; label: string }[], value: unknown): string {
  if (typeof value !== 'string') return '';
  return options.find((option) => option.value === value)?.label || value;
}

function lineStatusLabel(lineStatus: LineupCopyStatus['line_status']): string {
  if (lineStatus === 'default_by_opponent') return 'Default by Opponent';
  if (lineStatus === 'default_by_us') return 'Default by Us';
  return 'Normal';
}

function lineupHealthSummary(match: MatchEventRecord): { score: number; label: string; display: string } {
  if (match.match_status === 'Completed' || match.match_status === 'Cancelled') return { score: -1, label: 'Not Available', display: 'N/A' };
  const score = (match as MatchEventRecord & { lineup_health_score?: number | null }).lineup_health_score;
  if (typeof score !== 'number' || !Number.isFinite(score)) return { score: -1, label: 'Not Available', display: 'N/A' };
  return { score, label: score >= 75 ? 'Good' : 'Needs Attention', display: String(Math.round(score)) };
}

function normalizeNullable(value: string): string | null {
  return value.trim() === '' ? null : value.trim();
}

function normalizeEmail(value: string | null | undefined): string {
  return String(value || '').trim().toLowerCase();
}

function isValidEmailAddress(value: string | null | undefined): boolean {
  const email = normalizeEmail(value);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 254;
}

function defaultOpponentCaptainSubject(match: MatchEventRecord, teamName: string): string {
  return `${match.opponent_name || 'Upcoming match'} vs ${teamName || 'CourtCaptain'} on ${formatShortDate(match.match_date) || 'match day'}`;
}

async function authHeaders(): Promise<Record<string, string>> {
  const session = (await supabase.auth.getSession()).data.session;
  return session ? { Authorization: `Bearer ${session.access_token}` } : {};
}

const matchBulkFields: MatchBulkField[] = [
  'match_date',
  'opponent_name',
  'match_site_type',
  'match_time',
  'warmup_time',
  'facility_id',
  'court_numbers',
  'match_status',
  'match_classification',
  'opponent_captain_names',
  'opponent_captain_email',
  'opponent_captain_phone',
  'opponent_co_captain_name',
  'opponent_co_captain_email',
  'opponent_co_captain_phone',
  'opponent_tennis_record_roster_url',
  'notes_summary',
];

const matchBulkLabels: Record<MatchBulkField, string> = {
  match_date: 'Date',
  opponent_name: 'Opponent',
  match_site_type: 'Home/Away',
  match_time: 'Match Time',
  warmup_time: 'Warm-up',
  facility_id: 'Facility',
  court_numbers: 'Courts',
  match_status: 'Match Status',
  match_classification: 'Classification',
  opponent_captain_names: 'Opposing Captain Name',
  opponent_captain_email: 'Opposing Captain Email',
  opponent_captain_phone: 'Opposing Captain Phone',
  opponent_co_captain_name: 'Opposing Co-Captain Name',
  opponent_co_captain_email: 'Opposing Co-Captain Email',
  opponent_co_captain_phone: 'Opposing Co-Captain Phone',
  opponent_tennis_record_roster_url: 'TennisRecord URL',
  notes_summary: 'Notes',
};

function matchBulkCellKey(matchId: string, field: MatchBulkField): string {
  return `${matchId}:${field}`;
}

export function MatchScheduling({
  lookups,
  initialMatchId,
  initialScoreEditorOpen = false,
  initialEditMatchId = null,
  initialCreateMatchOpen = false,
  initialTeamFilterId,
  mode = 'list',
  onMatchOpened,
  onEditMatchOpened,
  onCreateMatchOpened,
  onOpenMatchDetail,
  onEditMatchRoute,
  onTeamFilterApplied,
  onOpenLineup,
  onOpenAvailability,
  onBackToMatches,
  onOpenTeamEvents,
  backLabel = 'Team Events',
  breadcrumbOrigin = 'team-events',
  isWorkflowFullScreen = false,
  onExitWorkflowFullScreen,
  onEditMatchSaved,
  onCreateMatchSaved,
  onCreateMatchCancelled,
  onScoreEditorClosed,
  timeFormat,
  userPreferences,
  readOnly = false,
  canSendEmail = true,
}: MatchSchedulingProps) {
  const auditUserId = useAuditUserId();
  const { isOwner, writableTeamIds } = useAuth();
  const defaultViewState = useMemo<MatchSchedulingViewState>(() => ({
    teamFilters: [],
    matchStatusFilters: [],
    dateRangePreset: 'upcoming',
    dateFromFilter: '',
    dateToFilter: '',
    matchSort: { field: 'match_date', direction: 'asc' },
  }), []);
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState<MatchSchedulingViewState>({
    userId: auditUserId,
    routeKey: mode === 'detail' ? 'match-detail' : 'matches',
    contextKey: mode === 'detail' ? initialMatchId || 'unknown-match' : 'default',
    defaultState: defaultViewState,
  });
  const [matches, setMatches] = useState<MatchEventRecord[]>([]);
  const [teams, setTeams] = useState<TeamDetail[]>([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(initialMatchId || null);
  const [teamFilters, setTeamFilters] = useState<string[]>(initialTeamFilterId ? [initialTeamFilterId] : savedViewState.teamFilters);
  const [matchStatusFilters, setMatchStatusFilters] = useState<MatchStatus[]>(savedViewState.matchStatusFilters);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>(savedViewState.dateRangePreset);
  const [dateFromFilter, setDateFromFilter] = useState(savedViewState.dateFromFilter);
  const [dateToFilter, setDateToFilter] = useState(savedViewState.dateToFilter);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [bulkEditorOpen, setBulkEditorOpen] = useState(false);
  const [bulkEditorSaving, setBulkEditorSaving] = useState(false);
  const [bulkDrafts, setBulkDrafts] = useState<Record<string, MatchBulkDraft>>({});
  const [bulkErrors, setBulkErrors] = useState<Record<string, string>>({});
  const [scoreEditorOpen, setScoreEditorOpen] = useState(initialScoreEditorOpen);
  const [editing, setEditing] = useState<MatchEventRecord | null>(null);
  const [form, setForm] = useState<MatchForm>(emptyMatchForm);
  const [savedMatchForm, setSavedMatchForm] = useState<MatchForm>(emptyMatchForm);
  const [warmupManuallyEdited, setWarmupManuallyEdited] = useState(false);
  const [groupMeEndManuallyEdited, setGroupMeEndManuallyEdited] = useState(false);
  const [siteManuallyEdited, setSiteManuallyEdited] = useState(false);
  const [matchSort, setMatchSort] = useState<{ field: MatchSortField; direction: SortDirection }>(savedViewState.matchSort);
  const [snack, setSnack] = useState<Snack | null>(null);
  const [groupMeImageUploading, setGroupMeImageUploading] = useState(false);
  const [manualCopyMessage, setManualCopyMessage] = useState('');
  const [opponentRefreshLoading, setOpponentRefreshLoading] = useState(false);
  const [detailMenuAnchor, setDetailMenuAnchor] = useState<HTMLElement | null>(null);
  const [rowMenuAnchor, setRowMenuAnchor] = useState<HTMLElement | null>(null);
  const [rowMenuMatch, setRowMenuMatch] = useState<MatchEventRecord | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [participationCount, setParticipationCount] = useState(0);
  const [availabilitySummary, setAvailabilitySummary] = useState<AvailabilitySummary>({ total: 0, known: 0, available: 0 });
  const [lifecycleHistory, setLifecycleHistory] = useState<MatchLifecycleHistoryRow[]>([]);
  const [matchResultSummary, setMatchResultSummary] = useState<MatchResultSummary | null>(null);
  const [recommendationSummaries, setRecommendationSummaries] = useState<Record<string, RecommendationFreshness>>({});
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState<OpposingCaptainEmailDraft | null>(null);
  const [emailMatch, setEmailMatch] = useState<MatchEventRecord | null>(null);
  const [emailHistory, setEmailHistory] = useState<OpposingCaptainEmailSend[]>([]);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSaveRecipient, setEmailSaveRecipient] = useState(true);
  const restoredViewStateKey = useRef('');
  const openedInitialEditMatchId = useRef<string | null>(null);
  const openedInitialCreateMatch = useRef(false);
  const [viewStateReady, setViewStateReady] = useState(false);

  const selectedMatch = useMemo(() => matches.find((match) => match.id === selectedMatchId) || null, [matches, selectedMatchId]);
  const matchDraftPersistence = useDraftPersistence({
    userId: auditUserId,
    screenType: 'match-scheduling',
    entityId: editing?.id || 'new',
    value: form,
    initialValue: savedMatchForm,
    enabled: dialogOpen && !readOnly,
  });
  const teamById = useMemo(() => new Map(teams.map((team) => [team.id, team])), [teams]);
  const writableTeams = useMemo(
    () => isOwner ? teams : teams.filter((team) => writableTeamIds.includes(team.id)),
    [isOwner, teams, writableTeamIds],
  );
  const activeTeamOptions = useMemo(() => teams.map((team) => ({
    label: team.team_name,
    value: team.id,
    season_id: team.season_id,
  })), [teams]);
  const accessibleTeamIds = useMemo(() => lookups.teams.map((team) => team.value), [lookups.teams]);
  const activeDateRange = useMemo(() => dateRangeBounds(dateRangePreset, dateFromFilter, dateToFilter), [dateFromFilter, dateRangePreset, dateToFilter]);
  const filtersAreDefault = teamFilters.length === 0 && matchStatusFilters.length === 0 && dateRangePreset === 'upcoming' && !dateFromFilter && !dateToFilter;

  const matchSortValue = useCallback(
    (match: MatchEventRecord, field: MatchSortField): string => {
      if (field === 'team_id') return lookupLabel(lookups, 'teams', match.team_id);
      if (field === 'match_status') return optionLabel(matchStatusOptions, match.match_status || 'Draft');
      if (field === 'lineup_status') return optionLabel(lineupStatusOptions, match.lineup_status || 'Not Started');
      if (field === 'health') return lineupHealthSummary(match).score.toString().padStart(3, '0');
      return match.match_date || '';
    },
    [lookups],
  );

  const sortedMatches = useMemo(() => {
    const direction = dateRangePreset === 'past' && matchSort.field === 'match_date' ? 'desc' : matchSort.direction;
    const multiplier = direction === 'asc' ? 1 : -1;
    const filtered = matches.filter((match) => {
      if (teamFilters.length > 0 && !teamFilters.includes(match.team_id)) return false;
      if (matchStatusFilters.length > 0 && !matchStatusFilters.includes(match.match_status || 'Draft')) return false;
      if (activeDateRange.from && match.match_date < activeDateRange.from) return false;
      if (activeDateRange.to && match.match_date > activeDateRange.to) return false;
      return true;
    });
    return filtered.sort((a, b) =>
      matchSortValue(a, matchSort.field).localeCompare(matchSortValue(b, matchSort.field), undefined, { numeric: true, sensitivity: 'base' }) * multiplier,
    );
  }, [activeDateRange.from, activeDateRange.to, dateRangePreset, matches, matchSort, matchSortValue, matchStatusFilters, teamFilters]);

  function bulkOriginalValue(match: MatchEventRecord, field: MatchBulkField): string {
    const value = match[field as keyof MatchEventRecord];
    if (field === 'match_time' || field === 'warmup_time') return toTimeInput(value as string | null | undefined);
    return value == null ? '' : String(value);
  }

  const writableTeamIdSet = useMemo(() => new Set(writableTeams.map((team) => team.id)), [writableTeams]);
  const bulkEditableMatches = useMemo(
    () => sortedMatches.filter((match) => writableTeamIdSet.has(match.team_id)),
    [sortedMatches, writableTeamIdSet],
  );
  const bulkValue = (match: MatchEventRecord, field: MatchBulkField): string => (
    bulkDrafts[match.id]?.[field] ?? bulkOriginalValue(match, field)
  );
  const isBulkCellChanged = (match: MatchEventRecord, field: MatchBulkField): boolean => (
    bulkValue(match, field) !== bulkOriginalValue(match, field)
  );
  const bulkPendingCellCount = useMemo(
    () => bulkEditableMatches.reduce((count, match) => count + matchBulkFields.filter((field) => isBulkCellChanged(match, field)).length, 0),
    [bulkDrafts, bulkEditableMatches],
  );

  const updateBulkCell = (match: MatchEventRecord, field: MatchBulkField, value: string) => {
    setBulkDrafts((current) => ({
      ...current,
      [match.id]: {
        ...(current[match.id] || {}),
        [field]: value,
      },
    }));
    setBulkErrors((current) => {
      const next = { ...current };
      delete next[matchBulkCellKey(match.id, field)];
      return next;
    });
  };

  const validateBulkEditor = (): Record<string, string> => {
    const nextErrors: Record<string, string> = {};
    bulkEditableMatches.forEach((match) => {
      if (!bulkDrafts[match.id]) return;
      const date = bulkValue(match, 'match_date');
      const opponent = bulkValue(match, 'opponent_name').trim();
      const matchTime = bulkValue(match, 'match_time');
      const warmupTime = bulkValue(match, 'warmup_time');
      const status = bulkValue(match, 'match_status');
      const classification = bulkValue(match, 'match_classification');
      const url = bulkValue(match, 'opponent_tennis_record_roster_url').trim();
      const captainEmail = bulkValue(match, 'opponent_captain_email').trim();
      const coCaptainEmail = bulkValue(match, 'opponent_co_captain_email').trim();
      if (!date) nextErrors[matchBulkCellKey(match.id, 'match_date')] = 'Date is required.';
      if (!opponent) nextErrors[matchBulkCellKey(match.id, 'opponent_name')] = 'Opponent is required.';
      if (matchTime && !toTimeInput(matchTime)) nextErrors[matchBulkCellKey(match.id, 'match_time')] = 'Match time is invalid.';
      if (warmupTime && !toTimeInput(warmupTime)) nextErrors[matchBulkCellKey(match.id, 'warmup_time')] = 'Warm-up time is invalid.';
      if (!matchStatusOptions.some((option) => option.value === status)) nextErrors[matchBulkCellKey(match.id, 'match_status')] = 'Choose a valid match status.';
      if (!matchClassificationOptions.some((option) => option.value === classification)) nextErrors[matchBulkCellKey(match.id, 'match_classification')] = 'Choose a valid classification.';
      if (url && !/^https?:\/\/(www\.)?tennisrecord\.com\/adult\/teamprofile\.aspx/i.test(url)) {
        nextErrors[matchBulkCellKey(match.id, 'opponent_tennis_record_roster_url')] = 'Use a TennisRecord adult team profile URL.';
      }
      if (captainEmail && !isValidEmailAddress(captainEmail)) {
        nextErrors[matchBulkCellKey(match.id, 'opponent_captain_email')] = 'Enter a valid email address.';
      }
      if (coCaptainEmail && !isValidEmailAddress(coCaptainEmail)) {
        nextErrors[matchBulkCellKey(match.id, 'opponent_co_captain_email')] = 'Enter a valid email address.';
      }
    });
    return nextErrors;
  };

  const openBulkEditor = () => {
    if (readOnly || writableTeams.length === 0) return;
    setBulkDrafts({});
    setBulkErrors({});
    setBulkEditorOpen(true);
  };

  const cancelBulkEditor = () => {
    if (bulkPendingCellCount > 0 && !window.confirm('Discard unsaved match edits?')) return;
    setBulkEditorOpen(false);
    setBulkDrafts({});
    setBulkErrors({});
  };

  const saveBulkEditor = async () => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    if (bulkPendingCellCount === 0) {
      setSnack({ severity: 'success', message: 'No match updates to save.' });
      return;
    }
    const nextErrors = validateBulkEditor();
    setBulkErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setSnack({ severity: 'error', message: 'Fix highlighted match cells before saving.' });
      return;
    }

    setBulkEditorSaving(true);
    const changedMatches = bulkEditableMatches.filter((match) => matchBulkFields.some((field) => isBulkCellChanged(match, field)));
    const results = await Promise.all(changedMatches.map((match) => {
      const payload = matchBulkFields.reduce<Record<string, string | null>>((acc, field) => {
        if (!isBulkCellChanged(match, field)) return acc;
        const value = bulkValue(match, field).trim();
        if (field === 'match_time' || field === 'warmup_time') acc[field] = value ? toTimeInput(value) || value : null;
        else if (field === 'facility_id') acc[field] = value || null;
        else if (field === 'opponent_captain_email' || field === 'opponent_co_captain_email') acc[field] = normalizeEmail(value) || null;
        else if (field === 'opponent_tennis_record_roster_url' || field === 'opponent_captain_names' || field === 'opponent_captain_phone' || field === 'opponent_co_captain_name' || field === 'opponent_co_captain_phone' || field === 'court_numbers' || field === 'notes_summary') acc[field] = value || null;
        else acc[field] = value;
        return acc;
      }, {});
      payload.updated_by = auditUserId || null;
      return supabase.from('match_events').update(payload).eq('id', match.id);
    }));
    setBulkEditorSaving(false);

    const error = results.find((result) => result.error)?.error;
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    setSnack({ severity: 'success', message: `${bulkPendingCellCount} match update${bulkPendingCellCount === 1 ? '' : 's'} saved.` });
    setBulkEditorOpen(false);
    setBulkDrafts({});
    setBulkErrors({});
    await loadMatches();
  };

  const clearFilters = () => {
    setTeamFilters([]);
    setMatchStatusFilters([]);
    setDateRangePreset('upcoming');
    setDateFromFilter('');
    setDateToFilter('');
    setSavedViewState(defaultViewState);
  };

  const toggleMatchSort = (field: MatchSortField) => {
    setMatchSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const SortHeader = ({ field, label }: { field: MatchSortField; label: string }) => (
    <TableCell sx={{ fontWeight: 700 }}>
      <TableSortLabel active={matchSort.field === field} direction={matchSort.field === field ? matchSort.direction : 'asc'} onClick={() => toggleMatchSort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const loadTeams = useCallback(async () => {
    if (accessibleTeamIds.length === 0) {
      setTeams([]);
      setTeamsLoaded(true);
      return;
    }
    setTeamsLoaded(false);
    const { data, error } = await supabase
      .from('teams')
      .select('id, team_name, team_color, organization_id, season_id, league_configuration_id, home_facility_id, league_name, home_match_opponent_message_template, default_warmup_minutes, default_groupme_event_reminders')
      .is('deleted_at', null)
      .eq('is_active', true)
      .in('id', accessibleTeamIds)
      .order('team_name');

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      setTeamsLoaded(true);
      return;
    }
    setTeams((data || []) as unknown as TeamDetail[]);
    setTeamsLoaded(true);
  }, [accessibleTeamIds]);

  const loadMatches = useCallback(async () => {
    if (accessibleTeamIds.length === 0) {
      setMatches([]);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('match_events')
      .select('*')
      .is('deleted_at', null)
      .in('team_id', accessibleTeamIds)
      .order('match_date', { ascending: true });
    setLoading(false);

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    const rows = (data || []) as unknown as MatchEventRecord[];
    setMatches(rows);
  }, [accessibleTeamIds, selectedMatchId]);

  useEffect(() => {
    if (matches.length === 0) {
      setRecommendationSummaries({});
      return;
    }

    const loadRecommendationSummaries = async () => {
      const teamIds = [...new Set(matches.map((match) => match.team_id))];
      const { data, error } = await supabase
        .from('lineup_recommendation_analyses')
        .select('affected_match_ids, generated_at, updated_at')
        .in('team_id', teamIds)
        .is('deleted_at', null)
        .order('generated_at', { ascending: false })
        .limit(250);

      if (error) {
        setRecommendationSummaries({});
        return;
      }

      const next: Record<string, RecommendationFreshness> = {};
      const rows = (data || []) as unknown as RecommendationSummaryRow[];
      for (const match of matches) {
        if (match.match_status === 'Completed' || match.match_status === 'Cancelled') {
          next[match.id] = { status: 'N/A', detail: optionLabel(matchStatusOptions, match.match_status) };
          continue;
        }
        const analysis = rows.find((row) => Array.isArray(row.affected_match_ids) && row.affected_match_ids.includes(match.id));
        if (!analysis?.generated_at) {
          next[match.id] = { status: 'Never Generated', detail: 'No saved recommendation' };
          continue;
        }
        const generatedAt = new Date(analysis.generated_at);
        const updatedAt = match.updated_at ? new Date(match.updated_at) : null;
        const stale = updatedAt && updatedAt.getTime() > generatedAt.getTime();
        next[match.id] = {
          status: stale ? 'Needs Refresh' : 'Fresh',
          detail: stale ? 'Match changed' : `Generated ${formatShortDate(analysis.generated_at.slice(0, 10))}`,
        };
      }
      setRecommendationSummaries(next);
    };

    void loadRecommendationSummaries();
  }, [matches]);

  useEffect(() => {
    void loadTeams();
    void loadMatches();
  }, [loadTeams, loadMatches]);

  useEffect(() => {
    if (initialMatchId) {
      setSelectedMatchId(initialMatchId);
      onMatchOpened?.();
    }
  }, [initialMatchId, onMatchOpened]);

  useEffect(() => {
    if (initialScoreEditorOpen) setScoreEditorOpen(true);
  }, [initialScoreEditorOpen]);

  useEffect(() => {
    if (!initialEditMatchId || readOnly || matches.length === 0) return;
    if (openedInitialEditMatchId.current === initialEditMatchId) return;
    const match = matches.find((candidate) => candidate.id === initialEditMatchId);
    if (match) {
      openedInitialEditMatchId.current = initialEditMatchId;
      openEdit(match);
      onEditMatchOpened?.();
    }
  }, [initialEditMatchId, matches, onEditMatchOpened, readOnly]);

  useEffect(() => {
    if (!initialCreateMatchOpen || readOnly || !teamsLoaded || teams.length === 0) return;
    if (openedInitialCreateMatch.current) return;
    openedInitialCreateMatch.current = true;
    openCreate();
    onCreateMatchOpened?.();
  }, [initialCreateMatchOpen, onCreateMatchOpened, readOnly, teams, teamsLoaded]);

  useEffect(() => {
    if (!initialTeamFilterId || !teamsLoaded) return;
    setTeamFilters(teams.some((team) => team.id === initialTeamFilterId) ? [initialTeamFilterId] : []);
    setSelectedMatchId(null);
    onTeamFilterApplied?.();
  }, [initialTeamFilterId, onTeamFilterApplied, teams, teamsLoaded]);

  useEffect(() => {
    if (initialTeamFilterId || !viewStateReady || !teamsLoaded) return;
    setTeamFilters((current) => {
      const valid = current.filter((teamId) => teams.some((team) => team.id === teamId));
      return valid.length === current.length ? current : valid;
    });
  }, [initialTeamFilterId, teams, teamsLoaded, viewStateReady]);

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setTeamFilters(initialTeamFilterId ? [initialTeamFilterId] : savedViewState.teamFilters);
    setMatchStatusFilters(savedViewState.matchStatusFilters);
    setDateRangePreset(savedViewState.dateRangePreset);
    setDateFromFilter(savedViewState.dateFromFilter);
    setDateToFilter(savedViewState.dateToFilter);
    setMatchSort(savedViewState.matchSort);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, initialTeamFilterId, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({
      teamFilters,
      matchStatusFilters,
      dateRangePreset,
      dateFromFilter,
      dateToFilter,
      matchSort,
    });
  }, [dateFromFilter, dateRangePreset, dateToFilter, matchSort, matchStatusFilters, setSavedViewState, teamFilters, viewStateKey, viewStateReady]);

  usePersistentScrollRestoration({
    userId: auditUserId,
    routeKey: mode === 'detail' ? 'match-detail' : 'matches',
    contextKey: mode === 'detail' ? selectedMatchId || initialMatchId || 'unknown-match' : 'default',
    ready: !loading && (mode === 'list' || Boolean(selectedMatch)),
  });

  const applyTeamDefaults = (teamId: string, current: MatchForm): MatchForm => {
    const team = teamById.get(teamId);
    return {
      ...current,
      team_id: teamId,
      season_id: team?.season_id || '',
      league_configuration_id: team?.league_configuration_id || '',
    };
  };

  const suggestedSiteType = (teamId: string, facilityId: string): MatchSiteType | null => {
    const team = teamById.get(teamId);
    if (!team || !facilityId || !team.home_facility_id) return null;
    return facilityId === team.home_facility_id ? 'Home' : 'Away';
  };

  const homeFacilityForTeam = (teamId: string): string => teamById.get(teamId)?.home_facility_id || '';
  const defaultWarmupMinutesForTeam = (teamId: string): number => teamById.get(teamId)?.default_warmup_minutes ?? 30;
  const defaultWarmupTimeForTeam = (teamId: string, matchTime: string): string => (matchTime ? subtractMinutes(matchTime, defaultWarmupMinutesForTeam(teamId)) : '');
  const groupMeStartTimeForForm = (current: MatchForm): string => (
    current.groupme_event_time_source === 'warmup_time'
      ? current.warmup_time || current.match_time
      : current.match_time || current.warmup_time
  );
  const defaultGroupMeEndTimeForForm = (current: MatchForm): string => addMinutesToTimeInput(current.match_time || groupMeStartTimeForForm(current), 120);
  const groupMeEventTimeOptionLabel = (source: MatchForm['groupme_event_time_source']): string => {
    const value = source === 'warmup_time' ? form.warmup_time : form.match_time;
    const label = source === 'warmup_time' ? 'Warmup' : 'Match';
    return value ? `${formatTime(value, timeFormat)} (${label})` : `${label} time not set`;
  };
  const isDefaultGroupMeEndTime = (current: MatchForm): boolean => {
    if (!current.groupme_event_end_time) return true;
    const selectedStartTime = groupMeStartTimeForForm(current);
    const defaultFromMatchTime = defaultGroupMeEndTimeForForm(current);
    const defaultFromSelectedStartTime = selectedStartTime ? addMinutesToTimeInput(selectedStartTime, 120) : '';
    return current.groupme_event_end_time === defaultFromMatchTime || current.groupme_event_end_time === defaultFromSelectedStartTime;
  };

  const openCreate = () => {
    if (readOnly) return;
    const filteredTeamId = teamFilters.length === 1 && teams.some((team) => team.id === teamFilters[0]) ? teamFilters[0] : '';
    const initialTeamId = filteredTeamId || teams[0]?.id || '';
    setEditing(null);
    setWarmupManuallyEdited(false);
    setGroupMeEndManuallyEdited(false);
    setSiteManuallyEdited(false);
    const nextForm = applyTeamDefaults(initialTeamId, { ...emptyMatchForm, team_id: initialTeamId });
    setForm(nextForm);
    setSavedMatchForm(nextForm);
    setDialogOpen(true);
  };

  const openEdit = (match: MatchEventRecord) => {
    if (readOnly) return;
    setEditing(match);
    setWarmupManuallyEdited(Boolean(match.warmup_time));
    setSiteManuallyEdited(true);
    const source = match.groupme_event_time_source || 'match_time';
    const matchTime = toTimeInput(match.match_time);
    const warmupTime = toTimeInput(match.warmup_time);
    const groupMeEventEndTime = toTimeInput(match.groupme_event_end_time);
    const nextForm: MatchForm = {
      team_id: match.team_id,
      season_id: match.season_id,
      league_configuration_id: match.league_configuration_id,
      match_classification: match.match_classification,
      match_site_type: match.match_site_type || 'Unknown',
      match_date: match.match_date,
      match_time: matchTime,
      warmup_time: warmupTime,
      opponent_name: match.opponent_name || '',
      opponent_average_edr: (match.manual_opponent_edr ?? match.opponent_average_edr) == null ? '' : String(match.manual_opponent_edr ?? match.opponent_average_edr),
      opponent_tennis_record_roster_url: match.opponent_tennis_record_roster_url || '',
      opponent_edr_overall: match.opponent_edr_overall == null ? '' : String(match.opponent_edr_overall),
      opponent_edr_source: match.opponent_edr_source || 'manual',
      opponent_rating_basis: match.opponent_rating_basis || 'avg_edr',
      opponent_subjective_difficulty: match.opponent_subjective_difficulty == null ? '3' : String(match.opponent_subjective_difficulty),
      opponent_captain_names: match.opponent_captain_names || '',
      opponent_captain_email: match.opponent_captain_email || '',
      opponent_captain_phone: match.opponent_captain_phone || '',
      opponent_co_captain_name: match.opponent_co_captain_name || '',
      opponent_co_captain_email: match.opponent_co_captain_email || '',
      opponent_co_captain_phone: match.opponent_co_captain_phone || '',
      facility_id: match.facility_id || '',
      court_numbers: match.court_numbers || '',
      groupme_event_title: match.groupme_event_title || '',
      groupme_event_description: match.groupme_event_description || '',
      groupme_event_image_url: match.groupme_event_image_url || '',
      groupme_event_time_source: source,
      groupme_event_end_time: groupMeEventEndTime || addMinutesToTimeInput(matchTime || (source === 'warmup_time' ? warmupTime : matchTime || warmupTime), match.groupme_event_duration_minutes ?? 120),
      groupme_reminder_mode: match.groupme_reminder_mode || 'team_default',
      groupme_custom_reminders: normalizeGroupMeReminders(match.groupme_custom_reminders),
      match_status: match.match_status || 'Draft',
      notes_summary: match.notes_summary || '',
    };
    setGroupMeEndManuallyEdited(Boolean(groupMeEventEndTime) && !isDefaultGroupMeEndTime(nextForm));
    setForm(nextForm);
    setSavedMatchForm(nextForm);
    setDialogOpen(true);
  };

  const updateForm = (patch: Partial<MatchForm>) => setForm((current) => ({ ...current, ...patch }));
  const uploadGroupMeImage = async (file: File | null) => {
    if (!file) return;
    if (!form.team_id) {
      setSnack({ severity: 'warning', message: 'Select a team before uploading a GroupMe image.' });
      return;
    }
    setGroupMeImageUploading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Sign in again before uploading a GroupMe image.');
      const body = new FormData();
      body.append('teamId', form.team_id);
      body.append('image', file);
      const response = await fetch('/api/groupme/upload-image', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      const result = await response.json() as { imageUrl?: string; error?: string };
      if (!response.ok || !result.imageUrl) throw new Error(result.error || 'Unable to upload image to GroupMe.');
      updateForm({ groupme_event_image_url: result.imageUrl });
      setSnack({ severity: 'success', message: 'Image uploaded to GroupMe.' });
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to upload image to GroupMe.' });
    } finally {
      setGroupMeImageUploading(false);
    }
  };
  const updateGroupMeReminder = (index: number, nextValue: string) => {
    setForm((current) => {
      const reminders = normalizeGroupMeReminders(current.groupme_custom_reminders);
      const next = [...reminders];
      if (nextValue === '') next.splice(index, 1);
      else next[index] = Number(nextValue);
      return { ...current, groupme_custom_reminders: normalizeGroupMeReminders(next) };
    });
  };

  const changeTeam = (teamId: string) => {
    setForm((current) => {
      const next = applyTeamDefaults(teamId, current);
      if (current.match_site_type === 'Home') {
        next.facility_id = homeFacilityForTeam(teamId);
      }
      if (!warmupManuallyEdited && next.match_time) {
        next.warmup_time = defaultWarmupTimeForTeam(teamId, next.match_time);
      }
      if (!siteManuallyEdited && next.facility_id) {
        next.match_site_type = suggestedSiteType(teamId, next.facility_id) || 'Unknown';
      }
      return next;
    });
  };

  const changeMatchSiteType = (matchSiteType: MatchSiteType) => {
    setSiteManuallyEdited(true);
    setForm((current) => ({
      ...current,
      match_site_type: matchSiteType,
      facility_id: matchSiteType === 'Home' ? homeFacilityForTeam(current.team_id) : '',
    }));
  };

  const changeFacility = (facilityId: string) => {
    setForm((current) => ({
      ...current,
      facility_id: facilityId,
      match_site_type: siteManuallyEdited ? current.match_site_type : suggestedSiteType(current.team_id, facilityId) || 'Unknown',
    }));
  };

  const changeMatchTime = (matchTime: string) => {
    setForm((current) => {
      const shouldRefreshGroupMeEndTime = !groupMeEndManuallyEdited || isDefaultGroupMeEndTime(current);
      const next = {
        ...current,
        match_time: matchTime,
        warmup_time: !warmupManuallyEdited && matchTime ? defaultWarmupTimeForTeam(current.team_id, matchTime) : current.warmup_time,
      };
      return {
        ...next,
        groupme_event_end_time: shouldRefreshGroupMeEndTime ? defaultGroupMeEndTimeForForm(next) : current.groupme_event_end_time,
      };
    });
  };

  const resetWarmupTime = () => {
    setForm((current) => ({
      ...current,
      warmup_time: defaultWarmupTimeForTeam(current.team_id, current.match_time),
      groupme_event_end_time: groupMeEndManuallyEdited ? current.groupme_event_end_time : defaultGroupMeEndTimeForForm({
        ...current,
        warmup_time: defaultWarmupTimeForTeam(current.team_id, current.match_time),
      }),
    }));
    setWarmupManuallyEdited(false);
  };

  const saveMatch = async () => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    if (!form.team_id || !form.season_id || !form.league_configuration_id) {
      setSnack({ severity: 'error', message: 'Team is required.' });
      return;
    }
    if (!form.match_classification) {
      setSnack({ severity: 'error', message: 'Match Classification is required.' });
      return;
    }
    if (!form.match_date) {
      setSnack({ severity: 'error', message: 'Match Date is required.' });
      return;
    }
    if (!form.match_status) {
      setSnack({ severity: 'error', message: 'Match Status is required.' });
      return;
    }
    if (form.opponent_average_edr !== '' && (!Number.isFinite(Number(form.opponent_average_edr)) || Number(form.opponent_average_edr) < 1 || Number(form.opponent_average_edr) > 7)) {
      setSnack({ severity: 'error', message: 'Manual Opponent EDR must be between 1.00 and 7.00.' });
      return;
    }
    if (!Number.isFinite(Number(form.opponent_subjective_difficulty)) || Number(form.opponent_subjective_difficulty) < 1 || Number(form.opponent_subjective_difficulty) > 5) {
      setSnack({ severity: 'error', message: 'Opponent difficulty must be between 1 and 5.' });
      return;
    }
    if (form.opponent_tennis_record_roster_url && !/^https?:\/\/(www\.)?tennisrecord\.com\/adult\/teamprofile\.aspx/i.test(form.opponent_tennis_record_roster_url.trim())) {
      setSnack({ severity: 'error', message: 'Opponent TennisRecord Roster URL must be an adult team profile page.' });
      return;
    }
    if (form.opponent_captain_email && !isValidEmailAddress(form.opponent_captain_email)) {
      setSnack({ severity: 'error', message: 'Opposing Captain Email must be a valid email address.' });
      return;
    }
    if (form.opponent_co_captain_email && !isValidEmailAddress(form.opponent_co_captain_email)) {
      setSnack({ severity: 'error', message: 'Opposing Co-Captain Email must be a valid email address.' });
      return;
    }
    if (form.groupme_event_time_source === 'warmup_time' && !form.warmup_time) {
      setSnack({ severity: 'error', message: 'Warmup Time is required when GroupMe event time uses warmup time.' });
      return;
    }
    if (form.groupme_event_end_time && !toTimeInput(form.groupme_event_end_time)) {
      setSnack({ severity: 'error', message: 'GroupMe End Time must be a valid time.' });
      return;
    }

    const manualOpponentEdr = form.opponent_average_edr === '' ? null : Number(form.opponent_average_edr);
    const tennisRecordOpponentEdr = form.opponent_edr_overall === '' ? null : Number(Number(form.opponent_edr_overall).toFixed(2));
    const payload = {
      team_id: form.team_id,
      season_id: form.season_id,
      league_configuration_id: form.league_configuration_id,
      match_classification: form.match_classification,
      match_site_type: form.match_site_type || 'Unknown',
      match_date: form.match_date,
      match_time: normalizeNullable(form.match_time),
      warmup_time: normalizeNullable(form.warmup_time),
      opponent_name: form.opponent_name.trim() || 'TBD',
      opponent_average_edr: form.opponent_edr_source === 'tennis_record' ? tennisRecordOpponentEdr : manualOpponentEdr,
      manual_opponent_edr: manualOpponentEdr,
      opponent_edr_source: form.opponent_edr_source,
      opponent_rating_basis: form.opponent_rating_basis,
      opponent_subjective_difficulty: Number(form.opponent_subjective_difficulty),
      opponent_tennis_record_roster_url: normalizeNullable(form.opponent_tennis_record_roster_url),
      opponent_captain_names: normalizeNullable(form.opponent_captain_names),
      opponent_captain_email: normalizeEmail(form.opponent_captain_email) || null,
      opponent_captain_phone: normalizeNullable(form.opponent_captain_phone),
      opponent_co_captain_name: normalizeNullable(form.opponent_co_captain_name),
      opponent_co_captain_email: normalizeEmail(form.opponent_co_captain_email) || null,
      opponent_co_captain_phone: normalizeNullable(form.opponent_co_captain_phone),
      facility_id: form.facility_id || null,
      court_numbers: normalizeNullable(form.court_numbers),
      groupme_event_title: normalizeNullable(form.groupme_event_title),
      groupme_event_description: normalizeNullable(form.groupme_event_description),
      groupme_event_image_url: normalizeNullable(form.groupme_event_image_url),
      groupme_event_time_source: form.groupme_event_time_source,
      groupme_event_end_time: normalizeNullable(form.groupme_event_end_time || defaultGroupMeEndTimeForForm(form)),
      groupme_event_duration_minutes: null,
      groupme_reminder_mode: form.groupme_reminder_mode,
      groupme_custom_reminders: form.groupme_reminder_mode === 'custom' ? normalizeGroupMeReminders(form.groupme_custom_reminders) : null,
      match_status: form.match_status,
      ...(editing?.id
        ? { updated_by: auditUserId || null }
        : { lineup_status: 'Not Started', created_by: auditUserId || null, updated_by: auditUserId || null }),
      notes_summary: normalizeNullable(form.notes_summary),
    };

    const operation = editing?.id
      ? supabase.from('match_events').update(payload).eq('id', editing.id)
      : supabase.from('match_events').insert(payload).select('id').single();

    const { data, error } = await operation;
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    setDialogOpen(false);
    setSavedMatchForm(form);
    matchDraftPersistence.clearDraft();
    const saveMessage = 'Match saved.';
    setSnack({ severity: saveMessage.includes('failed') ? 'warning' : 'success', message: saveMessage });
    await loadMatches();
    if (!editing?.id && data && typeof data === 'object' && 'id' in data) {
      const newMatchId = String((data as { id: string }).id);
      const { error: availabilityError } = await supabase.rpc('initialize_availability_for_match', { p_match_event_id: newMatchId });
      if (availabilityError) {
        setSnack({ severity: 'error', message: availabilityError.message });
      }
      setSelectedMatchId(newMatchId);
      onCreateMatchSaved?.();
    } else if (editing?.id) {
      setSelectedMatchId(editing.id);
      onEditMatchSaved?.();
    }
  };

  const refreshOpponentTennisRecord = async () => {
    if (!editing?.id) {
      setSnack({ severity: 'error', message: 'Save the match before refreshing TennisRecord opponent data.' });
      return;
    }
    if (!form.opponent_tennis_record_roster_url.trim()) {
      setSnack({ severity: 'error', message: 'Add an opponent TennisRecord roster URL before refreshing.' });
      return;
    }
    setOpponentRefreshLoading(true);
    const session = (await supabase.auth.getSession()).data.session;
    const response = await fetch('/api/tennisrecord/opponent-refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({ matchEventId: editing.id, url: form.opponent_tennis_record_roster_url }),
    });
    const result = await response.json() as { parsed?: { overall?: number | null; rosterCount?: number; ratedPlayerCount?: number }; effectiveOpponentEdr?: number | null; error?: string };
    setOpponentRefreshLoading(false);
    if (!response.ok) {
      setSnack({ severity: 'error', message: result.error || 'Unable to refresh opponent TennisRecord roster.' });
      await loadMatches();
      return;
    }
    setSnack({ severity: 'success', message: `Opponent TennisRecord EDR refreshed${result.parsed?.overall ? ` (${formatEdr(result.parsed.overall)})` : ''}.` });
    await loadMatches();
    setForm((current) => ({
      ...current,
      opponent_edr_overall: result.parsed?.overall == null ? current.opponent_edr_overall : String(result.parsed.overall),
      opponent_edr_source: current.opponent_edr_source === 'tennis_record' ? 'tennis_record' : current.opponent_edr_source,
    }));
  };

  const unpublishGroupMeEventBeforeDelete = async (match: MatchEventRecord): Promise<boolean> => {
    if (!match.groupme_event_id) return true;
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) {
      setSnack({ severity: 'error', message: 'Sign in again before deleting this linked GroupMe match.' });
      return false;
    }
    const response = await fetch('/api/groupme/events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'unpublish',
        items: [{ kind: 'match', id: match.id }],
        reason: 'Unpublished because the CourtCaptain match was deleted.',
      }),
    });
    const result = await response.json() as { summary?: { failed?: number; skipped?: number }; results?: Array<{ status: string; reason?: string }>; error?: string };
    if (!response.ok || (result.summary?.failed || 0) > 0 || (result.summary?.skipped || 0) > 0) {
      const reason = result.error || result.results?.find((item) => item.status !== 'unpublished')?.reason || 'Unable to unpublish the linked GroupMe event.';
      setSnack({ severity: 'error', message: reason });
      return false;
    }
    return true;
  };

  const softDeleteMatch = async (match: MatchEventRecord) => {
    if (readOnly) return;
    const linkedWarning = match.groupme_event_id
      ? ' This will also unpublish the linked GroupMe event. Republishing later will create a new GroupMe event.'
      : '';
    if (!window.confirm(`Soft delete match against ${match.opponent_name || 'TBD'}?${linkedWarning}`)) return;
    if (!(await unpublishGroupMeEventBeforeDelete(match))) return;
    const { error } = await supabase
      .from('match_events')
      .update({ deleted_at: new Date().toISOString(), delete_reason: 'Deleted from Phase 2 Match Scheduling UI' })
      .eq('id', match.id);

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: 'Match soft deleted.' });
    if (selectedMatchId === match.id) setSelectedMatchId(null);
    await loadMatches();
  };

  const selectedTeam = selectedMatch ? teamById.get(selectedMatch.team_id) : null;
  const selectedMatchLabel = selectedMatch ? `vs ${selectedMatch.opponent_name || 'TBD'}` : 'Match Detail';
  const matchRouteContext = matchCanonicalContext(selectedMatchLabel, selectedMatch ? `/matches/${selectedMatch.id}` : undefined, breadcrumbOrigin);
  const matchBackDestination = resolveContextBackDestination(
    matchRouteContext,
    backLabel === 'Command Center'
      ? { label: 'Command Center', page: 'command-center', path: '/' }
      : backLabel === 'Planning Board'
        ? { label: 'Planning Board', page: 'planning-board', path: '/planning-board' }
        : null,
    { label: 'Team Events', page: 'team-events', path: '/team-events' },
  );

  const loadDetailSummaries = useCallback(async () => {
    if (!selectedMatch?.id || mode !== 'detail') {
      setParticipationCount(0);
      setAvailabilitySummary({ total: 0, known: 0, available: 0 });
      setLifecycleHistory([]);
      setMatchResultSummary(null);
      return;
    }

    const [participationResult, availabilityResult, historyResult, resultSummaryResult] = await Promise.all([
      supabase
        .from('player_participation_events')
        .select('id', { count: 'exact', head: true })
        .eq('match_event_id', selectedMatch.id)
        .is('deleted_at', null),
      supabase
        .from('availability_records')
        .select('availability_status')
        .eq('match_event_id', selectedMatch.id)
        .is('deleted_at', null),
      supabase
        .from('match_lifecycle_history')
        .select('id, previous_lifecycle_state, new_lifecycle_state, changed_at, changed_by, change_reason')
        .eq('match_event_id', selectedMatch.id)
        .order('changed_at', { ascending: true }),
      supabase
        .from('match_results')
        .select('match_event_id, result_status, updated_at, updated_by, our_team_score, opponent_team_score, team_result, team_result_value, calculated_result_snapshot')
        .eq('match_event_id', selectedMatch.id)
        .is('deleted_at', null)
        .maybeSingle(),
    ]);

    if (!participationResult.error) setParticipationCount(participationResult.count || 0);
    if (!availabilityResult.error) {
      const rows = ((availabilityResult.data || []) as Array<{ availability_status?: string | null }>);
      setAvailabilitySummary({
        total: rows.length,
        known: rows.filter((row) => row.availability_status && row.availability_status !== 'unknown').length,
        available: rows.filter((row) => row.availability_status === 'available').length,
      });
    }
    if (!historyResult.error) setLifecycleHistory((historyResult.data || []) as unknown as MatchLifecycleHistoryRow[]);
    if (!resultSummaryResult.error) setMatchResultSummary((resultSummaryResult.data || null) as MatchResultSummary | null);
  }, [mode, selectedMatch?.id]);

  useEffect(() => {
    void loadDetailSummaries();
  }, [loadDetailSummaries]);

  const loadFacilityForMessage = async (facilityId: string | null): Promise<FacilityMessageDetail | null> => {
    if (!facilityId) return null;
    const { data, error } = await supabase
      .from('facilities')
      .select('facility_name, address_line1, address_line2, city, state, zip_code, google_maps_url, warmup_booking_url')
      .eq('id', facilityId)
      .is('deleted_at', null)
      .maybeSingle();

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return null;
    }
    return (data || null) as FacilityMessageDetail | null;
  };

  const loadCaptainNamesForMessage = async (teamId: string): Promise<string> => {
    const { data: assignmentData, error: assignmentError } = await supabase
      .from('team_captain_assignments')
      .select('app_user_id, assignment_role, is_primary_captain, created_at')
      .eq('team_id', teamId)
      .eq('is_active', true)
      .is('deleted_at', null)
      .in('assignment_role', ['captain', 'co_captain', 'assistant_captain'])
      .order('is_primary_captain', { ascending: false });

    if (assignmentError) {
      setSnack({ severity: 'error', message: assignmentError.message });
      return '';
    }

    const assignments = ((assignmentData || []) as unknown as CaptainAssignmentMessageRow[]).filter((row) => row.app_user_id);
    const appUserIds = [...new Set(assignments.map((row) => row.app_user_id))];
    if (appUserIds.length === 0) return '';

    const { data: appUserData, error: appUserError } = await supabase
      .from('app_users')
      .select('id, display_name')
      .in('id', appUserIds)
      .eq('is_active', true)
      .is('deleted_at', null);

    if (appUserError) {
      setSnack({ severity: 'error', message: appUserError.message });
      return '';
    }

    const appUserById = new Map(((appUserData || []) as unknown as AppUserMessageRow[]).map((user) => [user.id, user.display_name || '']));
    const names = assignments
      .map((assignment) => appUserById.get(assignment.app_user_id) || '')
      .filter(Boolean);

    return formatNameList([...new Set(names)]);
  };

  const buildOpponentCaptainMessage = async (match: MatchEventRecord) => {
    const team = teamById.get(match.team_id);
    const template = team?.home_match_opponent_message_template?.trim() || defaultOpponentCaptainTemplate;
    const facilityId = match.facility_id || (match.match_site_type === 'Home' ? team?.home_facility_id || null : null);
    const [facility, captainNames] = await Promise.all([
      loadFacilityForMessage(facilityId || null),
      loadCaptainNamesForMessage(match.team_id),
    ]);

    const tokens = {
      CaptainNames: captainNames,
      TeamName: team?.team_name || '',
      LeagueName: team?.league_name || '',
      OpponentTeam: match.opponent_name || '',
      MatchDate: formatMatchDate(match.match_date),
      MatchTime: formatTime(match.match_time, timeFormat),
      WarmupTime: formatTime(match.warmup_time, timeFormat),
      WarmupMinutes: warmupMinutes(match.match_time, match.warmup_time),
      FacilityName: facility?.facility_name || '',
      FacilityAddress: facilityAddress(facility),
      GoogleMapsLink: facility?.google_maps_url || '',
      CourtNumbers: match.court_numbers || '',
      WarmupBookingLink: facility?.warmup_booking_url || '',
    };
    const message = applyTemplate(template, tokens);
    const includesWarmupBookingLink = template.includes('{WarmupBookingLink}');
    const missing = [
      !match.match_time ? 'missing match time' : '',
      !match.match_date ? 'missing match date' : '',
      !facilityId || !facility ? 'missing facility' : '',
      !facility?.google_maps_url ? 'missing Google Maps link' : '',
      !captainNames ? 'No active captains found for this team.' : '',
      includesWarmupBookingLink && !facility?.warmup_booking_url ? 'missing warmup booking link' : '',
    ].filter(Boolean);
    return { message, missing, teamName: team?.team_name || '' };
  };

  const copyOpponentCaptainMessage = async (match: MatchEventRecord) => {
    const { message, missing } = await buildOpponentCaptainMessage(match);

    try {
      await navigator.clipboard.writeText(message);
      setSnack({
        severity: missing.length > 0 ? 'warning' : 'success',
        message: missing.length > 0 ? `Opponent captain message copied. Warning: ${missing.join(', ')}.` : 'Opponent captain message copied.',
      });
    } catch {
      setManualCopyMessage(message);
      setSnack({
        severity: missing.length > 0 ? 'warning' : 'info',
        message: missing.length > 0 ? `Clipboard blocked. Warning: ${missing.join(', ')}.` : 'Clipboard blocked. Copy the message from the dialog.',
      });
    }
  };

  const loadOpponentCaptainEmailHistory = async (matchId: string) => {
    const { data, error } = await supabase
      .from('opposing_captain_email_sends')
      .select('id,gmail_email,recipient_email,subject,sent_at,send_status')
      .eq('match_event_id', matchId)
      .order('sent_at', { ascending: false })
      .limit(5);
    if (error) {
      setEmailHistory([]);
      return;
    }
    setEmailHistory((data || []) as unknown as OpposingCaptainEmailSend[]);
  };

  const openOpponentCaptainEmail = async (match: MatchEventRecord) => {
    if (!canSendEmail) {
      setSnack({ severity: 'error', message: 'Only the workspace owner can send email from CourtCaptain.' });
      return;
    }
    const response = await fetch('/api/gmail/status', { headers: await authHeaders() });
    const status = await response.json().catch(() => ({}));
    if (!response.ok) {
      setSnack({ severity: 'error', message: status.error || 'Unable to load Gmail connection.' });
      return;
    }
    if (!status.connected) {
      setSnack({ severity: 'warning', message: 'Connect Gmail in Settings before sending opposing captain messages.' });
      return;
    }
    const { message, missing, teamName } = await buildOpponentCaptainMessage(match);
    setEmailMatch(match);
    setEmailSaveRecipient(!match.opponent_captain_email);
    setEmailDraft({
      from: status.email || '',
      to: match.opponent_captain_email || '',
      subject: defaultOpponentCaptainSubject(match, teamName),
      body: message,
      missing,
    });
    await loadOpponentCaptainEmailHistory(match.id);
    setEmailDialogOpen(true);
  };

  const sendOpponentCaptainEmail = async () => {
    if (!emailMatch || !emailDraft) return;
    if (!isValidEmailAddress(emailDraft.to)) {
      setSnack({ severity: 'error', message: 'Enter a valid opposing captain email address.' });
      return;
    }
    if (!emailDraft.subject.trim() || !emailDraft.body.trim()) {
      setSnack({ severity: 'error', message: 'Subject and message are required.' });
      return;
    }
    setEmailSending(true);
    try {
      const response = await fetch('/api/gmail/opposing-captain/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await authHeaders()),
        },
        body: JSON.stringify({
          matchEventId: emailMatch.id,
          to: emailDraft.to,
          subject: emailDraft.subject,
          body: emailDraft.body,
          saveRecipient: emailSaveRecipient,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Unable to send Gmail message.');
      setSnack({ severity: 'success', message: `Message sent to ${payload.to || emailDraft.to}.` });
      setEmailDialogOpen(false);
      await loadMatches();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to send Gmail message.' });
    } finally {
      setEmailSending(false);
    }
  };

  const copyMatchDetails = async (match: MatchEventRecord) => {
    const team = teamById.get(match.team_id);
    const details = [
      `${lookupLabel(lookups, 'teams', match.team_id)} vs ${match.opponent_name || 'TBD'}`,
      `${formatMatchDate(match.match_date)}${formatTime(match.match_time, timeFormat) ? ` at ${formatTime(match.match_time, timeFormat)}` : ''}`,
      match.warmup_time ? `Warm-up: ${formatTime(match.warmup_time, timeFormat)}` : '',
      `${match.match_site_type || 'Unknown'} match`,
      `Organization/Season: ${[team ? lookupLabel(lookups, 'organizations', team.organization_id) : '', lookupLabel(lookups, 'seasons', match.season_id), optionLabel(matchClassificationOptions, match.match_classification)].filter(Boolean).join(' • ')}`,
      lookupLabel(lookups, 'facilities', match.facility_id) ? `Facility: ${lookupLabel(lookups, 'facilities', match.facility_id)}` : '',
      match.court_numbers ? `${listLabel(match.court_numbers, 'Court Number', 'Court Numbers')}: ${match.court_numbers}` : '',
      match.opponent_captain_names ? `Opposing Captain Name: ${match.opponent_captain_names}` : '',
      match.opponent_captain_email ? `Opposing Captain Email: ${match.opponent_captain_email}` : '',
      match.opponent_captain_phone ? `Opposing Captain Phone: ${match.opponent_captain_phone}` : '',
      match.opponent_co_captain_name ? `Opposing Co-Captain Name: ${match.opponent_co_captain_name}` : '',
      match.opponent_co_captain_email ? `Opposing Co-Captain Email: ${match.opponent_co_captain_email}` : '',
      match.opponent_co_captain_phone ? `Opposing Co-Captain Phone: ${match.opponent_co_captain_phone}` : '',
    ].filter(Boolean).join('\n');

    try {
      await navigator.clipboard.writeText(details);
      setSnack({ severity: 'success', message: 'Match details copied.' });
    } catch {
      setManualCopyMessage(details);
      setSnack({ severity: 'info', message: 'Clipboard blocked. Copy the match details from the dialog.' });
    }
  };

  const copyMatchLineup = async (match: MatchEventRecord, external: boolean) => {
    const [positionsResult, entriesResult, statusesResult, standbyResult] = await Promise.all([
      supabase
        .from('league_configuration_positions')
        .select('id, position_code, display_order')
        .eq('league_configuration_id', match.league_configuration_id)
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('display_order', { ascending: true }),
      supabase
        .from('lineup_entries')
        .select('match_event_id, league_configuration_position_id, slot_number, player_id')
        .eq('match_event_id', match.id)
        .is('deleted_at', null),
      supabase
        .from('lineup_position_statuses')
        .select('league_configuration_position_id, line_status')
        .eq('match_event_id', match.id),
      supabase
        .from('standby_players')
        .select('player_id, notes')
        .eq('match_event_id', match.id)
        .is('deleted_at', null),
    ]);

    if (positionsResult.error || entriesResult.error || statusesResult.error || standbyResult.error) {
      setSnack({
        severity: 'error',
        message: positionsResult.error?.message || entriesResult.error?.message || statusesResult.error?.message || standbyResult.error?.message || 'Unable to copy lineup.',
      });
      return;
    }

    const entries = (entriesResult.data || []) as unknown as LineupCopyEntry[];
    const standby = (standbyResult.data || []) as unknown as LineupCopyStandby[];
    const playerIds = [...new Set([...entries.map((entry) => entry.player_id), ...standby.map((row) => row.player_id)])];
    const playersResult = playerIds.length > 0
      ? await supabase.from('players').select('id, first_name, last_name').in('id', playerIds).is('deleted_at', null)
      : { data: [], error: null };

    if (playersResult.error) {
      setSnack({ severity: 'error', message: playersResult.error.message });
      return;
    }

    const playersById = ((playersResult.data || []) as unknown as LineupCopyPlayer[]).reduce<Record<string, LineupCopyPlayer>>((acc, player) => {
      acc[player.id] = player;
      return acc;
    }, {});
    const statusesByPosition = ((statusesResult.data || []) as unknown as LineupCopyStatus[]).reduce<Record<string, LineupCopyStatus['line_status']>>((acc, status) => {
      acc[status.league_configuration_position_id] = status.line_status;
      return acc;
    }, {});
    const entriesByPosition = entries.reduce<Record<string, LineupCopyEntry[]>>((acc, entry) => {
      acc[entry.league_configuration_position_id] ||= [];
      acc[entry.league_configuration_position_id].push(entry);
      return acc;
    }, {});

    const header = `${lookupLabel(lookups, 'teams', match.team_id)} vs ${match.opponent_name || 'TBD'} on ${formatMatchDate(match.match_date)}${formatTime(match.match_time, timeFormat) ? ` at ${formatTime(match.match_time, timeFormat)}` : ''}`;
    const lines = [
      external ? 'External Lineup' : 'Internal Lineup',
      header,
      '',
      ...((positionsResult.data || []) as unknown as LineupCopyPosition[]).map((position) => {
        const lineStatus = statusesByPosition[position.id] || 'normal';
        if (lineStatus !== 'normal') return `${position.position_code}: ${lineStatusLabel(lineStatus)}`;
        const names = (entriesByPosition[position.id] || [])
          .sort((a, b) => a.slot_number - b.slot_number)
          .map((entry) => formatPlayerFirstLast(playersById[entry.player_id]))
          .filter(Boolean)
          .join(' / ');
        return `${position.position_code}: ${names || 'TBD'}`;
      }),
    ];

    if (!external && standby.length > 0) {
      lines.push(
        '',
        'Standby:',
        ...standby.map((row) => `- ${formatPlayerFirstLast(playersById[row.player_id]) || 'Unknown Player'}${row.notes ? ` (${row.notes})` : ''}`),
      );
    }

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setSnack({ severity: 'success', message: `${external ? 'External' : 'Internal'} lineup copied.` });
    } catch {
      setManualCopyMessage(lines.join('\n'));
      setSnack({ severity: 'info', message: 'Clipboard blocked. Copy the lineup from the dialog.' });
    }
  };

  const selectedMatchStatusParts = selectedMatch ? {
    created: formatDateTimeParts(selectedMatch.created_at),
    published: formatDateTimeParts(selectedMatch.lineup_published_at),
    completed: formatDateTimeParts(selectedMatch.completed_at),
    scoresSubmitted: formatDateTimeParts(matchResultSummary?.updated_at),
  } : null;

  const availabilityPercent = availabilitySummary.total > 0 ? Math.round((availabilitySummary.known / availabilitySummary.total) * 100) : null;
  const readinessScore = selectedMatch
    ? Math.min(100, Math.round(
      (availabilityPercent ?? 0) * 0.5
      + (selectedMatch.opponent_average_edr !== null ? 20 : 0)
      + (selectedMatch.lineup_status === 'Published' ? 30 : selectedMatch.lineup_status === 'Draft' ? 18 : 0),
    ))
    : 0;
  const readinessLabel = readinessScore >= 85 ? 'Ready' : readinessScore >= 60 ? 'Needs Review' : 'Not Ready';
  const readinessColor: 'success' | 'warning' | 'error' = readinessScore >= 85 ? 'success' : readinessScore >= 60 ? 'warning' : 'error';
  const lastRecommendationLabel = 'No recommendation generated';
  const timelineActor = (userId: string | null | undefined) => userTimelineLabel(lookupLabel(lookups, 'app_users', userId));
  const scoresSubmitted = matchResultSummary?.result_status === 'Submitted';

  const timelineSteps = selectedMatch ? [
    {
      key: 'scheduled',
      label: 'Scheduled',
      complete: true,
      current: selectedMatch.lineup_status === 'Not Started' || selectedMatch.lineup_status === 'Draft',
      icon: <CheckCircleIcon fontSize="small" />,
      parts: selectedMatchStatusParts?.created,
      actor: timelineActor(selectedMatch.created_by),
    },
    {
      key: 'published',
      label: 'Lineup Published',
      complete: Boolean(selectedMatch.lineup_published_at),
      current: selectedMatch.lineup_status === 'Published',
      icon: <CheckCircleIcon fontSize="small" />,
      parts: selectedMatchStatusParts?.published,
      actor: timelineActor(selectedMatch.lineup_published_by),
    },
    {
      key: 'scores-entered',
      label: 'Scores Entered',
      complete: selectedMatch.match_status === 'Scores Entered' || scoresSubmitted,
      current: selectedMatch.match_status === 'Scores Entered',
      icon: selectedMatch.match_status === 'Scores Entered' || scoresSubmitted ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />,
      parts: selectedMatch.match_status === 'Scores Entered' ? formatDateTimeParts(selectedMatch.updated_at) : null,
      actor: '',
    },
    {
      key: 'completed',
      label: 'Completed',
      complete: selectedMatch.match_status === 'Completed',
      current: selectedMatch.match_status === 'Completed',
      icon: selectedMatch.match_status === 'Completed' ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />,
      parts: selectedMatchStatusParts?.completed,
      actor: timelineActor(selectedMatch.completed_by),
    },
    {
      key: 'scores-submitted',
      label: 'Scores Submitted',
      complete: scoresSubmitted,
      current: scoresSubmitted,
      icon: scoresSubmitted ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />,
      parts: scoresSubmitted ? selectedMatchStatusParts?.scoresSubmitted : null,
      actor: scoresSubmitted ? timelineActor(matchResultSummary?.updated_by) : '',
    },
  ] : [];

  const renderBulkEditorCell = (match: MatchEventRecord, field: MatchBulkField) => {
    const value = bulkValue(match, field);
    const error = bulkErrors[matchBulkCellKey(match.id, field)];
    const changed = isBulkCellChanged(match, field);
    const commonSx = {
      minWidth: field === 'notes_summary' ? 260 : field === 'opponent_tennis_record_roster_url' || field === 'opponent_name' ? 220 : 150,
      bgcolor: changed ? 'warning.light' : undefined,
      '& .MuiInputBase-input': { py: 0.75, fontSize: 13 },
    };
    if (field === 'match_site_type') {
      return (
        <FormControl fullWidth size="small" error={Boolean(error)} sx={commonSx}>
          <Select value={value || 'Unknown'} inputProps={{ 'data-match-bulk-cell': 'true' }} onChange={(event) => updateBulkCell(match, field, event.target.value)}>
            {matchSiteTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }
    if (field === 'match_status') {
      return (
        <FormControl fullWidth size="small" error={Boolean(error)} sx={commonSx}>
          <Select value={value || 'Draft'} inputProps={{ 'data-match-bulk-cell': 'true' }} onChange={(event) => updateBulkCell(match, field, event.target.value)}>
            {matchStatusOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }
    if (field === 'match_classification') {
      return (
        <FormControl fullWidth size="small" error={Boolean(error)} sx={commonSx}>
          <Select value={value || 'regular_season'} inputProps={{ 'data-match-bulk-cell': 'true' }} onChange={(event) => updateBulkCell(match, field, event.target.value)}>
            {matchClassificationOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }
    if (field === 'facility_id') {
      return (
        <FormControl fullWidth size="small" error={Boolean(error)} sx={commonSx}>
          <Select value={value} inputProps={{ 'data-match-bulk-cell': 'true' }} onChange={(event) => updateBulkCell(match, field, event.target.value)}>
            <MenuItem value="">None</MenuItem>
            {lookups.facilities.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }
    return (
      <TextField
        size="small"
        value={value}
        error={Boolean(error)}
        helperText={error || ''}
        type={field === 'match_date' ? 'date' : field === 'match_time' || field === 'warmup_time' ? 'time' : 'text'}
        multiline={field === 'notes_summary'}
        minRows={field === 'notes_summary' ? 2 : undefined}
        InputLabelProps={field === 'match_date' ? { shrink: true } : undefined}
        sx={commonSx}
        onKeyDown={(event) => {
          if (event.key !== 'Tab' && event.key !== 'Enter') return;
          const inputs = Array.from(document.querySelectorAll<HTMLElement>('input[data-match-bulk-cell="true"], textarea[data-match-bulk-cell="true"], [role="combobox"] input[data-match-bulk-cell="true"]'));
          const index = inputs.indexOf(event.currentTarget);
          const next = inputs[index + (event.shiftKey ? -1 : 1)];
          if (next) {
            event.preventDefault();
            next.focus();
          }
        }}
        inputProps={{ 'data-match-bulk-cell': 'true' }}
        onChange={(event) => updateBulkCell(match, field, event.target.value)}
      />
    );
  };

  const DetailMeta = ({ icon, label, value }: { icon?: ReactNode; label: string; value: string }) => (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
      {icon && <Box sx={{ display: 'flex', color: 'text.secondary' }}>{icon}</Box>}
      <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>{value}</Typography>
      <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>{label}</Box>
    </Stack>
  );

  return (
    <Stack spacing={2} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <DraftRecoveryDialog
        open={Boolean(matchDraftPersistence.recoverableDraft)}
        updatedAt={matchDraftPersistence.recoverableDraft?.updatedAt}
        onRestore={() => {
          const restored = matchDraftPersistence.restoreDraft();
          if (restored) setForm(restored);
        }}
        onDiscard={matchDraftPersistence.discardDraft}
      />
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2 }}>
        <Box>
          {mode === 'detail' && (
            <BreadcrumbTrail
              items={buildBreadcrumbTrail(matchRouteContext)}
              onNavigate={(item) => {
                if (item.path === '/team-events') onOpenTeamEvents?.();
                if (item.path === '/planning-board') onBackToMatches?.();
              }}
            />
          )}
          {mode === 'detail' && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <ContextBackButton destination={matchBackDestination} onBack={() => onBackToMatches?.()} />
              {isWorkflowFullScreen && (
                <Button size="small" variant="text" startIcon={<FullscreenExitIcon />} onClick={onExitWorkflowFullScreen}>
                  Exit Full Screen
                </Button>
              )}
            </Stack>
          )}
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {mode === 'detail' ? 'Match Detail' : 'Match Scheduling'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {mode === 'detail'
              ? 'Review match details, notes, lineup actions, and captain communication.'
              : 'Create and manage team matches and schedules.'}
          </Typography>
        </Box>
        {mode === 'list' && !readOnly && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={openBulkEditor} disabled={bulkEditableMatches.length === 0}>
              Bulk Edit Matches
            </Button>
            <Button variant="outlined" startIcon={<PlaylistAddIcon />} onClick={() => setBulkImportOpen(true)} disabled={writableTeams.length === 0}>
              Bulk Add Matches
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={teams.length === 0}>
              Add Match
            </Button>
          </Stack>
        )}
      </Box>

      {teams.length === 0 && <Alert severity="info">Create a team before scheduling matches.</Alert>}

      {mode === 'list' && (
        <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 1.5 } }}>
          <Stack spacing={1.25}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr 1fr' }, gap: 1.25, alignItems: 'center' }}>
              <SeasonTeamMultiSelect lookups={lookups} value={teamFilters} onChange={setTeamFilters} teams={activeTeamOptions} />
              <FormControl fullWidth size="small">
                <InputLabel>Match Status</InputLabel>
                <Select
                  multiple
                  label="Match Status"
                  value={matchStatusFilters}
                  onChange={(event) => {
                    const value = event.target.value;
                    setMatchStatusFilters(typeof value === 'string' ? value.split(',') as MatchStatus[] : value as MatchStatus[]);
                  }}
                  renderValue={(selected) => (
                    selected.length === 0 || selected.length === matchStatusOptions.length
                      ? 'All Statuses'
                      : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => <Chip key={value} size="small" label={optionLabel(matchStatusOptions, value)} />)}
                        </Box>
                      )
                  )}
                >
                  {matchStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={matchStatusFilters.includes(option.value as MatchStatus)} />
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  label="Date Range"
                  value={dateRangePreset}
                  onChange={(event) => setDateRangePreset(event.target.value as DateRangePreset)}
                >
                  <MenuItem value="upcoming">Upcoming</MenuItem>
                  <MenuItem value="this_week">This Week</MenuItem>
                  <MenuItem value="next_30_days">Next 30 Days</MenuItem>
                  <MenuItem value="this_season">This Season</MenuItem>
                  <MenuItem value="past">Past Matches</MenuItem>
                  <MenuItem value="all">All Matches</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {dateRangePreset === 'custom' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(180px, 240px))' }, gap: 1.25 }}>
                <TextField
                  label="Date From"
                  type="date"
                  size="small"
                  value={dateFromFilter}
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => setDateFromFilter(event.target.value)}
                />
                <TextField
                  label="Date To"
                  type="date"
                  size="small"
                  value={dateToFilter}
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => setDateToFilter(event.target.value)}
                />
              </Box>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {sortedMatches.length} match{sortedMatches.length === 1 ? '' : 'es'}
              </Typography>
              {!filtersAreDefault && (
                <Button size="small" onClick={clearFilters}>Clear Filters</Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}

      {mode === 'list' && (
        <>
        <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 1.5 }}>
          {loading && <LinearProgress />}
          {sortedMatches.map((match) => {
            const health = lineupHealthSummary(match);
            const recommendation = recommendationSummaries[match.id] || { status: 'Never Generated', detail: 'No saved recommendation' };
            const siteLabel = match.match_site_type === 'Away' ? 'Away match' : match.match_site_type === 'Home' ? 'Home match' : 'Match site not set';
            const facilityLabel = match.groupme_location_text || lookupLabel(lookups, 'facilities', match.facility_id);
            return (
              <Paper key={match.id} variant="outlined" sx={{ p: 1.5, borderLeft: `5px solid ${matchAccentColor(match.match_status)}` }}>
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Tooltip title={siteLabel}>
                      <Box aria-label={siteLabel} sx={{ color: match.match_site_type === 'Away' ? 'warning.main' : match.match_site_type === 'Home' ? 'success.main' : 'text.secondary', pt: 0.25 }}>
                        {match.match_site_type === 'Away' ? <FlightTakeoffIcon fontSize="small" /> : match.match_site_type === 'Home' ? <HomeIcon fontSize="small" /> : <PlaceIcon fontSize="small" />}
                      </Box>
                    </Tooltip>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {formatShortDate(match.match_date)}{formatTime(match.match_time, timeFormat) ? ` • ${formatTime(match.match_time, timeFormat)}` : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{lookupLabel(lookups, 'teams', match.team_id)} <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>vs {match.opponent_name || 'TBD'}</Box></Typography>
                      {facilityLabel && <Typography variant="body2" color="text.secondary">{facilityLabel}{match.court_numbers ? ` • Court ${match.court_numbers}` : ''}</Typography>}
                      {match.opponent_average_edr !== null && <Chip size="small" variant="outlined" label={`Opponent EDR ${formatEdr(match.opponent_average_edr)}`} sx={{ mt: 0.75 }} />}
                    </Box>
                  </Stack>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Box><Typography variant="caption" color="text.secondary">Status</Typography><Chip size="small" color={matchStatusColor(match.match_status)} label={optionLabel(matchStatusOptions, match.match_status || 'Draft')} /></Box>
                    <Box><Typography variant="caption" color="text.secondary">Lineup</Typography><Chip size="small" color={lineupStatusColor(match.lineup_status)} label={optionLabel(lineupStatusOptions, match.lineup_status || 'Not Started')} /></Box>
                    <Box><Typography variant="caption" color="text.secondary">Health</Typography><Typography variant="body2" aria-label={`Lineup health ${health.label}`} sx={{ fontWeight: 800 }}>{health.display} <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>{health.label}</Box></Typography></Box>
                    <Box><Typography variant="caption" color="text.secondary">Recommendation</Typography><Typography variant="body2" sx={{ fontWeight: 800 }}>{recommendation.status}</Typography><Typography variant="caption" color="text.secondary">{recommendation.detail}</Typography></Box>
                  </Box>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="View Match Detail">
                      <IconButton aria-label="View Match Detail" color="primary" onClick={() => onOpenMatchDetail?.(match.id)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open Lineup Builder">
                      <IconButton aria-label="Open Lineup Builder" color="primary" onClick={() => onOpenLineup?.(match.id)}>
                        <FormatListBulletedIcon />
                      </IconButton>
                    </Tooltip>
                    {!readOnly && (
                      <Tooltip title="Edit Match">
                        <IconButton aria-label="Edit Match" onClick={() => onEditMatchRoute ? onEditMatchRoute(match.id) : openEdit(match)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="More Actions">
                      <IconButton
                        aria-label="More Actions"
                        onClick={(event) => {
                          setRowMenuAnchor(event.currentTarget);
                          setRowMenuMatch(match);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
          {sortedMatches.length === 0 && !loading && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>No matches found</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Adjust your filters or add a match.</Typography>
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                {!filtersAreDefault && <Button variant="outlined" onClick={clearFilters}>Clear Filters</Button>}
                {!readOnly && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Match</Button>}
              </Stack>
            </Paper>
          )}
        </Box>
        <Paper variant="outlined" sx={{ overflow: 'hidden', display: { xs: 'none', md: 'block' } }}>
          {loading && <LinearProgress />}
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1040 }}>
            <TableHead>
              <TableRow>
                <SortHeader field="match_date" label="Match" />
                <SortHeader field="match_status" label="Match Status" />
                <SortHeader field="lineup_status" label="Lineup" />
                <SortHeader field="health" label="Health" />
                <TableCell sx={{ fontWeight: 700 }}>Recommendation</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedMatches.map((match) => {
                const health = lineupHealthSummary(match);
                const recommendation = recommendationSummaries[match.id] || { status: 'Never Generated', detail: 'No saved recommendation' };
                const siteLabel = match.match_site_type === 'Away' ? 'Away match' : match.match_site_type === 'Home' ? 'Home match' : 'Match site not set';
                const facilityLabel = match.groupme_location_text || lookupLabel(lookups, 'facilities', match.facility_id);
                return (
                  <TableRow key={match.id} hover onClick={() => onOpenMatchDetail?.(match.id)} sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ borderLeft: `5px solid ${matchAccentColor(match.match_status)}`, py: 1.25, width: '44%' }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Tooltip title={siteLabel}>
                          <Box aria-label={siteLabel} sx={{ color: match.match_site_type === 'Away' ? 'warning.main' : match.match_site_type === 'Home' ? 'success.main' : 'text.secondary', pt: 0.25 }}>
                            {match.match_site_type === 'Away' ? <FlightTakeoffIcon fontSize="small" /> : match.match_site_type === 'Home' ? <HomeIcon fontSize="small" /> : <PlaceIcon fontSize="small" />}
                          </Box>
                        </Tooltip>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {formatShortDate(match.match_date)}{formatTime(match.match_time, timeFormat) ? ` • ${formatTime(match.match_time, timeFormat)}` : ''}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {lookupLabel(lookups, 'teams', match.team_id)} <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>vs {match.opponent_name || 'TBD'}</Box>
                          </Typography>
                          {facilityLabel && <Typography variant="caption" color="text.secondary" component="div">{facilityLabel}{match.court_numbers ? ` • Court ${match.court_numbers}` : ''}</Typography>}
                          {match.opponent_average_edr !== null && <Chip size="small" variant="outlined" label={`Opponent EDR ${formatEdr(match.opponent_average_edr)}`} sx={{ mt: 0.5 }} />}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><Chip size="small" color={matchStatusColor(match.match_status)} label={optionLabel(matchStatusOptions, match.match_status || 'Draft')} /></TableCell>
                    <TableCell><Chip size="small" color={lineupStatusColor(match.lineup_status)} label={optionLabel(lineupStatusOptions, match.lineup_status || 'Not Started')} /></TableCell>
                    <TableCell>
                      <Tooltip title="Lineup health, not win probability">
                        <Box aria-label={`Lineup health ${health.label}`}>
                          <Typography variant="body2" sx={{ fontWeight: 900 }}>{health.display}</Typography>
                          <Typography variant="caption" color="text.secondary">{health.label}</Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{recommendation.status}</Typography>
                      <Typography variant="caption" color="text.secondary">{recommendation.detail}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Match Detail">
                        <IconButton aria-label="View Match Detail" size="small" color="primary" onClick={(event) => {
                          event.stopPropagation();
                          onOpenMatchDetail?.(match.id);
                        }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open Lineup Builder">
                        <IconButton aria-label="Open Lineup Builder" size="small" color="primary" onClick={(event) => {
                          event.stopPropagation();
                          onOpenLineup?.(match.id);
                        }}>
                          <FormatListBulletedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!readOnly && (
                        <Tooltip title="Edit Match">
                          <IconButton aria-label="Edit Match" size="small" onClick={(event) => {
                            event.stopPropagation();
                            if (onEditMatchRoute) onEditMatchRoute(match.id);
                            else openEdit(match);
                          }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="More Actions">
                        <IconButton
                          aria-label="More Actions"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            setRowMenuAnchor(event.currentTarget);
                            setRowMenuMatch(match);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {sortedMatches.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 5, textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>No matches found</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Adjust your filters or add a match.</Typography>
                      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                        {!filtersAreDefault && <Button variant="outlined" onClick={clearFilters}>Clear Filters</Button>}
                        {!readOnly && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Match</Button>}
                      </Stack>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        </>
      )}

      <Menu
        id="match-row-actions-menu"
        anchorEl={rowMenuAnchor}
        open={Boolean(rowMenuAnchor)}
        onClose={() => {
          setRowMenuAnchor(null);
          setRowMenuMatch(null);
        }}
      >
        {rowMenuMatch && (
          <MenuItem onClick={() => {
            const match = rowMenuMatch;
            setRowMenuAnchor(null);
            setRowMenuMatch(null);
            void copyMatchDetails(match);
          }}>
            <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
            Copy Match Details
          </MenuItem>
        )}
        {rowMenuMatch && !readOnly && (
          <MenuItem
            onClick={() => {
              const match = rowMenuMatch;
              setRowMenuAnchor(null);
              setRowMenuMatch(null);
              void softDeleteMatch(match);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete Match
          </MenuItem>
        )}
      </Menu>

      {mode === 'detail' && (
        selectedMatch ? (
          <Stack spacing={1.5} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
            <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderLeft: `6px solid ${lookupTeamAccentColor(lookups, selectedMatch.team_id)}`, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                  <Tooltip title={selectedMatch.match_site_type === 'Away' ? 'Away match' : selectedMatch.match_site_type === 'Home' ? 'Home match' : 'Match site not set'}>
                    <Box
                      aria-label={selectedMatch.match_site_type === 'Away' ? 'Away match' : selectedMatch.match_site_type === 'Home' ? 'Home match' : 'Match site not set'}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: selectedMatch.match_site_type === 'Away' ? 'warning.light' : 'success.light',
                        color: selectedMatch.match_site_type === 'Away' ? 'warning.dark' : 'success.dark',
                        flexShrink: 0,
                      }}
                    >
                      {selectedMatch.match_site_type === 'Away' ? <FlightTakeoffIcon /> : <HomeIcon />}
                    </Box>
                  </Tooltip>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                      {lookupLabel(lookups, 'teams', selectedMatch.team_id)} vs {selectedMatch.opponent_name || 'TBD'}
                    </Typography>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      <DetailMeta icon={<CalendarTodayIcon fontSize="small" />} label="Match date" value={formatShortDate(selectedMatch.match_date)} />
                      <DetailMeta icon={<AccessTimeIcon fontSize="small" />} label="Match time" value={formatTime(selectedMatch.match_time, timeFormat) || 'Time TBD'} />
                      {selectedMatch.warmup_time && <DetailMeta icon={<AccessTimeIcon fontSize="small" />} label="Warm-up time" value={`Warm-up ${formatTime(selectedMatch.warmup_time, timeFormat)}`} />}
                    </Stack>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      {selectedTeam && <DetailMeta icon={<PublicIcon fontSize="small" />} label="Organization" value={lookupLabel(lookups, 'organizations', selectedTeam.organization_id)} />}
                      <DetailMeta icon={<CalendarTodayIcon fontSize="small" />} label="Season" value={lookupLabel(lookups, 'seasons', selectedMatch.season_id)} />
                      <DetailMeta icon={<ShieldIcon fontSize="small" />} label="Classification" value={optionLabel(matchClassificationOptions, selectedMatch.match_classification)} />
                    </Stack>
                    {(selectedMatch.groupme_location_text || lookupLabel(lookups, 'facilities', selectedMatch.facility_id)) && (
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
                        <PlaceIcon fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{selectedMatch.groupme_location_text || lookupLabel(lookups, 'facilities', selectedMatch.facility_id)}</Typography>
                      </Stack>
                    )}
                    {(selectedMatch.court_numbers || selectedMatch.opponent_captain_names || selectedMatch.opponent_captain_email || selectedMatch.opponent_captain_phone || selectedMatch.opponent_co_captain_name || selectedMatch.opponent_co_captain_email || selectedMatch.opponent_co_captain_phone) && (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} sx={{ mt: 1.5 }}>
                        {selectedMatch.court_numbers && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">{listLabel(selectedMatch.court_numbers, 'Court Number', 'Court Numbers')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{selectedMatch.court_numbers}</Typography>
                          </Box>
                        )}
                        {selectedMatch.opponent_captain_names && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Opposing Captain Name</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{selectedMatch.opponent_captain_names}</Typography>
                          </Box>
                        )}
                        {selectedMatch.opponent_captain_email && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Opposing Captain Email</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{selectedMatch.opponent_captain_email}</Typography>
                          </Box>
                        )}
                        {selectedMatch.opponent_captain_phone && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Opposing Captain Phone</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{selectedMatch.opponent_captain_phone}</Typography>
                          </Box>
                        )}
                        {selectedMatch.opponent_co_captain_name && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Opposing Co-Captain Name</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{selectedMatch.opponent_co_captain_name}</Typography>
                          </Box>
                        )}
                        {selectedMatch.opponent_co_captain_email && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Opposing Co-Captain Email</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{selectedMatch.opponent_co_captain_email}</Typography>
                          </Box>
                        )}
                        {selectedMatch.opponent_co_captain_phone && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Opposing Co-Captain Phone</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{selectedMatch.opponent_co_captain_phone}</Typography>
                          </Box>
                        )}
                      </Stack>
                    )}
                    {(selectedMatch.opponent_rating_basis || selectedMatch.opponent_subjective_difficulty != null || selectedMatch.opponent_edr_overall != null || selectedMatch.manual_opponent_edr != null || selectedMatch.opponent_tennis_record_roster_url) && (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                        <Chip
                          size="small"
                          color={selectedMatch.opponent_rating_basis === 'subjective' ? 'primary' : 'default'}
                          label={`Opponent Ranking: ${selectedMatch.opponent_rating_basis === 'subjective' ? 'Subjective Difficulty' : 'Average EDR'}`}
                        />
                        {selectedMatch.opponent_rating_basis === 'subjective' && (
                          <Chip size="small" variant="outlined" label={`Difficulty: ${opponentDifficultyLabel(selectedMatch.opponent_subjective_difficulty)}`} />
                        )}
                        <Chip
                          size="small"
                          color={selectedMatch.opponent_edr_source === 'tennis_record' ? 'success' : 'default'}
                          label={`EDR Source: ${selectedMatch.opponent_edr_source === 'tennis_record' ? 'TennisRecord' : 'Manual'}`}
                        />
                        {selectedMatch.opponent_edr_overall != null && <Chip size="small" variant="outlined" label={`TennisRecord Avg ${formatEdr(selectedMatch.opponent_edr_overall)}`} />}
                        {selectedMatch.opponent_edr_men != null && <Chip size="small" variant="outlined" label={`Men ${formatEdr(selectedMatch.opponent_edr_men)}`} />}
                        {selectedMatch.opponent_edr_women != null && <Chip size="small" variant="outlined" label={`Women ${formatEdr(selectedMatch.opponent_edr_women)}`} />}
                        {selectedMatch.opponent_rated_player_count != null && selectedMatch.opponent_roster_count != null && (
                          <Chip size="small" variant="outlined" label={`${selectedMatch.opponent_rated_player_count} of ${selectedMatch.opponent_roster_count} rated`} />
                        )}
                        {selectedMatch.opponent_edr_last_successful_refresh_at && (
                          <Chip size="small" variant="outlined" label={`Refreshed ${new Date(selectedMatch.opponent_edr_last_successful_refresh_at).toLocaleDateString()}`} />
                        )}
                      </Stack>
                    )}
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} alignItems="center">
                  {!readOnly && (
                    <Button variant="outlined" startIcon={<EditIcon />} onClick={() => onEditMatchRoute ? onEditMatchRoute(selectedMatch.id) : openEdit(selectedMatch)}>
                      Edit Match
                    </Button>
                  )}
                  <Tooltip title="More match actions">
                    <IconButton
                      aria-label="More match actions"
                      aria-controls={detailMenuAnchor ? 'match-detail-actions-menu' : undefined}
                      aria-expanded={detailMenuAnchor ? 'true' : undefined}
                      aria-haspopup="menu"
                      onClick={(event) => setDetailMenuAnchor(event.currentTarget)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Paper>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(300px, 0.8fr) minmax(0, 1.8fr)' }, gap: 1.5, width: '100%', maxWidth: '100%', minWidth: 0 }}>
              <Paper variant="outlined" sx={{ p: 2, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                {selectedMatch.match_status === 'Completed' && matchResultSummary?.match_event_id === selectedMatch.id && matchResultSummary.result_status === 'Submitted' ? <SavedMatchResultSummary
                  result={matchResultSummary} ourName={lookupLabel(lookups, 'teams', selectedMatch.team_id)} opponentName={selectedMatch.opponent_name || 'Opponent'}
                /> : <Stack spacing={1.5}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Match Readiness</Typography>
                    <Tooltip title="Readiness summarizes availability, lineup status, and opponent EDR context.">
                      <InfoOutlinedIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ width: 96, height: 96, borderRadius: '50%', border: '10px solid', borderColor: `${readinessColor}.main`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1 }}>{readinessScore}</Typography>
                        <Typography variant="caption">/100</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Detail label="Readiness" value={readinessLabel} />
                      <Detail label="Availability" value={availabilityPercent === null ? 'Pending' : `${availabilityPercent}%`} />
                      <Detail label="Opponent Ranking" value={selectedMatch.opponent_rating_basis === 'subjective' ? opponentDifficultyLabel(selectedMatch.opponent_subjective_difficulty) : (selectedMatch.opponent_average_edr === null ? 'Pending' : formatEdr(selectedMatch.opponent_average_edr))} />
                      <Detail label="Last Recommendation" value={lastRecommendationLabel} />
                    </Box>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Button variant="outlined" onClick={() => onOpenLineup?.(selectedMatch.id)}>
                      Go to Lineup Builder
                    </Button>
                    <Button variant="outlined" onClick={() => onOpenAvailability?.(selectedMatch.team_id, selectedMatch.id)}>
                      Player Availability
                    </Button>
                  </Stack>
                </Stack>}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Status Timeline</Typography>
                      <Tooltip title="Timeline shows saved lifecycle timestamps and actors when available.">
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </Tooltip>
                    </Stack>
                    <Button variant="outlined" size="small" endIcon={<HistoryIcon />} onClick={() => setHistoryDialogOpen(true)}>
                      View Full History
                    </Button>
                  </Stack>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(5, 150px)', md: 'repeat(5, 1fr)' }, gap: 1, overflowX: 'auto', width: '100%', maxWidth: '100%', minWidth: 0 }} role="list" aria-label="Match status timeline">
                    {timelineSteps.map((step, index) => (
                      <Box key={step.key} role="listitem" sx={{ minWidth: 150 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: step.complete ? 'success.main' : step.current ? 'success.dark' : 'action.hover', color: step.complete || step.current ? 'common.white' : 'text.secondary' }}>
                            {step.icon}
                          </Box>
                          {index < timelineSteps.length - 1 && <Divider sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />}
                        </Stack>
                        <Typography variant="body2" sx={{ fontWeight: 800, mt: 1 }}>{step.label}</Typography>
                        {step.parts ? (
                          <>
                            <Typography variant="caption" component="div" color="text.secondary">{step.parts.date}</Typography>
                            <Typography variant="caption" component="div" color="text.secondary">{step.parts.time}</Typography>
                            {step.actor && <Typography variant="caption" component="div" color="text.secondary">{step.actor}</Typography>}
                          </>
                        ) : (
                          <Typography variant="caption" component="div" color="text.secondary">Not yet</Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Paper>
            </Box>

            <MatchResults
              match={selectedMatch}
              onSaved={async () => {
                await loadMatches();
                await loadDetailSummaries();
              }}
              readOnly={readOnly}
              editorOpen={scoreEditorOpen}
              onEditorOpen={() => setScoreEditorOpen(true)}
              onEditorClose={() => {
                setScoreEditorOpen(false);
                onScoreEditorClosed?.();
              }}
            />

            <CalendarPublicationPanel
              kind="match"
              id={selectedMatch.id}
              readOnly={readOnly}
              onMessage={(severity, message) => setSnack({ severity, message })}
            />

            <Accordion variant="outlined" disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="match-participation-log-content" id="match-participation-log-header">
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Participation Log ({participationCount})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MatchParticipationEvents matchId={selectedMatch.id} lookups={lookups} />
              </AccordionDetails>
            </Accordion>

            <Accordion variant="outlined" disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="match-notes-content" id="match-notes-header">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Match Notes</Typography>
                  {selectedMatch.notes_summary && <Chip size="small" label="Has notes" />}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color={selectedMatch.notes_summary ? 'text.primary' : 'text.secondary'} sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedMatch.notes_summary || 'No match notes.'}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Stack>
        ) : (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              Match not found or still loading.
            </Typography>
          </Paper>
        )
      )}

      <Menu
        id="match-detail-actions-menu"
        anchorEl={detailMenuAnchor}
        open={Boolean(detailMenuAnchor)}
        onClose={() => setDetailMenuAnchor(null)}
      >
        {selectedMatch && canSendEmail && (
          <MenuItem
            onClick={() => {
              setDetailMenuAnchor(null);
              void copyMatchDetails(selectedMatch);
            }}
          >
            <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
            Copy Match Details
          </MenuItem>
        )}
        {selectedMatch && (
          <MenuItem
            onClick={() => {
              setDetailMenuAnchor(null);
              void copyOpponentCaptainMessage(selectedMatch);
            }}
          >
            <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
            Copy Captain Message
          </MenuItem>
        )}
        {selectedMatch && (
          <MenuItem
            onClick={() => {
              setDetailMenuAnchor(null);
              void openOpponentCaptainEmail(selectedMatch);
            }}
          >
            <EmailIcon fontSize="small" sx={{ mr: 1 }} />
            Email Captain
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setDetailMenuAnchor(null);
            setHistoryDialogOpen(true);
          }}
        >
          <HistoryIcon fontSize="small" sx={{ mr: 1 }} />
          View History
        </MenuItem>
        {selectedMatch && !readOnly && (
          <MenuItem
            onClick={() => {
              setDetailMenuAnchor(null);
              void softDeleteMatch(selectedMatch);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete Match
          </MenuItem>
        )}
      </Menu>

      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Match History</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            {timelineSteps.map((step) => (
              <Paper key={step.key} variant="outlined" sx={{ p: 1.25 }}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{step.label}</Typography>
                    {step.parts ? (
                      <Typography variant="caption" color="text.secondary">
                        {step.parts.date} {step.parts.time}{step.actor ? ` • ${step.actor}` : ''}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">Not yet</Typography>
                    )}
                  </Box>
                </Stack>
              </Paper>
            ))}
            {lifecycleHistory.map((row) => {
              const parts = formatDateTimeParts(row.changed_at);
              return (
                <Paper key={row.id} variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {row.previous_lifecycle_state ? `${row.previous_lifecycle_state} to ${row.new_lifecycle_state}` : row.new_lifecycle_state}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {parts ? `${parts.date} ${parts.time}` : row.changed_at}{timelineActor(row.changed_by) ? ` • ${timelineActor(row.changed_by)}` : ''}
                  </Typography>
                  {row.change_reason && <Typography variant="body2" color="text.secondary">{row.change_reason}</Typography>}
                </Paper>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Email Opposing Captain</DialogTitle>
        <DialogContent dividers>
          {emailDraft && (
            <Stack spacing={2}>
              {emailDraft.missing.length > 0 && (
                <Alert severity="warning">
                  {emailDraft.missing.join(', ')}
                </Alert>
              )}
              <TextField label="From" value={emailDraft.from} disabled fullWidth />
              <TextField
                label="To"
                value={emailDraft.to}
                onChange={(event) => setEmailDraft((current) => current ? { ...current, to: event.target.value } : current)}
                error={Boolean(emailDraft.to && !isValidEmailAddress(emailDraft.to))}
                helperText={emailDraft.to && !isValidEmailAddress(emailDraft.to) ? 'Enter a valid email address.' : ''}
                fullWidth
              />
              <FormControlLabel
                control={<Checkbox checked={emailSaveRecipient} onChange={(event) => setEmailSaveRecipient(event.target.checked)} />}
                label="Save this email on the match"
              />
              <TextField
                label="Subject"
                value={emailDraft.subject}
                onChange={(event) => setEmailDraft((current) => current ? { ...current, subject: event.target.value } : current)}
                fullWidth
              />
              <TextField
                label="Message"
                value={emailDraft.body}
                onChange={(event) => setEmailDraft((current) => current ? { ...current, body: event.target.value } : current)}
                multiline
                minRows={10}
                fullWidth
              />
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Recent Sends</Typography>
                {emailHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No opposing captain messages have been sent for this match.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {emailHistory.map((row) => (
                      <Paper key={row.id} variant="outlined" sx={{ p: 1.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{row.subject}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTimeParts(row.sent_at)?.date || row.sent_at} {formatDateTimeParts(row.sent_at)?.time || ''} • {row.gmail_email} to {row.recipient_email}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} disabled={emailSending}>Cancel</Button>
          <Button variant="contained" startIcon={<EmailIcon />} onClick={() => void sendOpponentCaptainEmail()} disabled={emailSending || !emailDraft?.to || !emailDraft?.subject.trim() || !emailDraft?.body.trim()}>
            {emailSending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Match' : 'Add Match'}</DialogTitle>
        <DialogContent>
          <DraftSavedIndicator savedAt={matchDraftPersistence.draftSavedAt} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
            <Typography variant="subtitle2" sx={{ gridColumn: '1 / -1', fontWeight: 800 }}>Match Setup</Typography>
            <FormControl fullWidth required>
              <InputLabel>Team</InputLabel>
              <Select label="Team" value={form.team_id} onChange={(event) => changeTeam(event.target.value)}>
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.team_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Organization" value={lookupLabel(lookups, 'organizations', teamById.get(form.team_id)?.organization_id)} disabled fullWidth />
            <TextField label="Season" value={lookupLabel(lookups, 'seasons', form.season_id)} disabled fullWidth />
            <TextField label="Match Format" value={lookupLabel(lookups, 'league_configurations', form.league_configuration_id)} disabled fullWidth />
            <FormControl fullWidth required>
              <InputLabel>Match Classification</InputLabel>
              <Select label="Match Classification" value={form.match_classification} onChange={(event) => updateForm({ match_classification: event.target.value })}>
                {matchClassificationOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="subtitle2" sx={{ gridColumn: '1 / -1', fontWeight: 800, mt: 1 }}>Schedule & Location</Typography>
            <FormControl fullWidth required>
              <InputLabel>Match Site Type</InputLabel>
              <Select
                label="Match Site Type"
                value={form.match_site_type}
                onChange={(event) => changeMatchSiteType(event.target.value as MatchSiteType)}
              >
                {matchSiteTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Match Date"
              type="date"
              value={form.match_date}
              required
              InputLabelProps={{ shrink: true }}
              onChange={(event) => updateForm({ match_date: event.target.value })}
            />
            <TextField
              label="Match Time"
              type="time"
              value={form.match_time}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => changeMatchTime(event.target.value)}
            />
            <TextField
              label="Warmup Time"
              type="time"
              value={form.warmup_time}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => {
                setWarmupManuallyEdited(true);
                updateForm({ warmup_time: event.target.value });
              }}
            />
            <Button variant="outlined" onClick={resetWarmupTime} disabled={!form.match_time} sx={{ alignSelf: 'center' }}>
              Reset to Team Default
            </Button>
            <FormControl fullWidth>
              <InputLabel>Facility</InputLabel>
              <Select label="Facility" value={form.facility_id} onChange={(event) => changeFacility(event.target.value)}>
                <MenuItem value="">None</MenuItem>
                {lookups.facilities.map((facility) => (
                  <MenuItem key={facility.value} value={facility.value}>
                    {facility.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Paper variant="outlined" sx={{ gridColumn: '1 / -1', p: 2, bgcolor: 'rgba(27, 94, 32, 0.03)' }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>GroupMe Publishing</Typography>
                <TextField
                  label="GroupMe Event Title"
                  value={form.groupme_event_title}
                  onChange={(event) => updateForm({ groupme_event_title: event.target.value })}
                  helperText="Optional. Leave blank to generate the title from the opponent and match site."
                  fullWidth
                />
                <TextField
                  label="GroupMe Event Description"
                  value={form.groupme_event_description}
                  onChange={(event) => updateForm({ groupme_event_description: event.target.value })}
                  helperText="Optional external text that will be sent to GroupMe. Internal match notes are not published."
                  multiline
                  minRows={2}
                  fullWidth
                />
                <TextField
                  label="GroupMe Event Image URL"
                  value={form.groupme_event_image_url}
                  onChange={(event) => updateForm({ groupme_event_image_url: event.target.value })}
                  helperText="Optional. Upload an image or paste a GroupMe image URL."
                  fullWidth
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    disabled={groupMeImageUploading || !form.team_id}
                  >
                    {groupMeImageUploading ? 'Uploading Image' : 'Upload Image'}
                    <input
                      hidden
                      type="file"
                      accept="image/jpeg,image/png,image/gif"
                      onChange={(event) => {
                        void uploadGroupMeImage(event.target.files?.[0] || null);
                        event.target.value = '';
                      }}
                    />
                  </Button>
                  {form.groupme_event_image_url ? (
                    <Button variant="text" onClick={() => updateForm({ groupme_event_image_url: '' })}>
                      Clear Image
                    </Button>
                  ) : null}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1fr) minmax(220px, 1fr)' }, gap: 1.5 }}>
                  <FormControl fullWidth>
                    <InputLabel>GroupMe Event Time</InputLabel>
                    <Select
                      label="GroupMe Event Time"
                      value={form.groupme_event_time_source}
                      onChange={(event) => {
                        const nextSource = event.target.value as MatchForm['groupme_event_time_source'];
                        setForm((current) => {
                          const next = { ...current, groupme_event_time_source: nextSource };
                          return { ...next, groupme_event_end_time: groupMeEndManuallyEdited ? current.groupme_event_end_time : defaultGroupMeEndTimeForForm(next) };
                        });
                      }}
                    >
                      <MenuItem value="warmup_time">{groupMeEventTimeOptionLabel('warmup_time')}</MenuItem>
                      <MenuItem value="match_time">{groupMeEventTimeOptionLabel('match_time')}</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="GroupMe End Time"
                    type="time"
                    value={form.groupme_event_end_time || defaultGroupMeEndTimeForForm(form)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Defaults to two hours after the match time."
                    onChange={(event) => {
                      setGroupMeEndManuallyEdited(true);
                      updateForm({ groupme_event_end_time: event.target.value });
                    }}
                  />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: form.groupme_reminder_mode === 'custom' ? 'minmax(180px, 0.8fr) repeat(2, minmax(180px, 1fr))' : 'minmax(180px, 0.8fr) minmax(220px, 1fr)' }, gap: 1.5, alignItems: 'center' }}>
                  <FormControl fullWidth>
                    <InputLabel>GroupMe Reminders</InputLabel>
                    <Select
                      label="GroupMe Reminders"
                      value={form.groupme_reminder_mode}
                      onChange={(event) => updateForm({ groupme_reminder_mode: event.target.value as MatchForm['groupme_reminder_mode'] })}
                    >
                      <MenuItem value="team_default">Use Team Defaults</MenuItem>
                      <MenuItem value="custom">Custom</MenuItem>
                    </Select>
                  </FormControl>
                  {form.groupme_reminder_mode === 'custom' ? (
                    [0, 1].map((index) => {
                      const reminders = normalizeGroupMeReminders(form.groupme_custom_reminders);
                      return (
                        <FormControl key={index} fullWidth>
                          <InputLabel>{index === 0 ? 'First Reminder' : 'Second Reminder'}</InputLabel>
                          <Select
                            label={index === 0 ? 'First Reminder' : 'Second Reminder'}
                            value={reminders[index] == null ? '' : String(reminders[index])}
                            onChange={(event) => updateGroupMeReminder(index, event.target.value)}
                          >
                            <MenuItem value="">No reminder</MenuItem>
                            {groupMeReminderOptions.map((option) => (
                              <MenuItem key={option.value} value={String(option.value)} disabled={reminders.includes(option.value) && reminders[index] !== option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Team default: {groupMeReminderSummary(teamById.get(form.team_id)?.default_groupme_event_reminders)}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>
            <Typography variant="subtitle2" sx={{ gridColumn: '1 / -1', fontWeight: 800, mt: 1 }}>Opponent Details</Typography>
            <TextField label="Opponent Name" value={form.opponent_name} onChange={(event) => updateForm({ opponent_name: event.target.value })} />
            <FormControl fullWidth>
              <InputLabel>Opponent Ranking Basis</InputLabel>
              <Select
                label="Opponent Ranking Basis"
                value={form.opponent_rating_basis}
                onChange={(event) => updateForm({ opponent_rating_basis: event.target.value as OpponentRatingBasis })}
              >
                {opponentRatingBasisOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Expected Opponent Difficulty</InputLabel>
              <Select
                label="Expected Opponent Difficulty"
                value={form.opponent_subjective_difficulty}
                onChange={(event) => updateForm({ opponent_subjective_difficulty: event.target.value })}
              >
                {opponentSubjectiveDifficultyOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Opponent EDR Source</InputLabel>
              <Select
                label="Opponent EDR Source"
                value={form.opponent_edr_source}
                onChange={(event) => updateForm({ opponent_edr_source: event.target.value as 'manual' | 'tennis_record' })}
              >
                <MenuItem value="manual">Use manual opponent EDR</MenuItem>
                <MenuItem value="tennis_record">Use TennisRecord average</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Manual Opponent EDR"
              type="number"
              value={form.opponent_average_edr}
              inputProps={{ step: 0.01, min: 1, max: 7 }}
              onChange={(event) => updateForm({ opponent_average_edr: event.target.value })}
            />
            <TextField
              label="Opponent TennisRecord Roster URL"
              value={form.opponent_tennis_record_roster_url}
              onChange={(event) => updateForm({ opponent_tennis_record_roster_url: event.target.value })}
              helperText={editing?.opponent_edr_last_successful_refresh_at ? `Last refreshed ${new Date(editing.opponent_edr_last_successful_refresh_at).toLocaleString()}` : editing?.opponent_edr_refresh_error || ''}
              error={Boolean(editing?.opponent_edr_refresh_error && editing.opponent_edr_refresh_status === 'failed')}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Button variant="outlined" disabled={!editing?.id || opponentRefreshLoading || !form.opponent_tennis_record_roster_url.trim()} onClick={() => void refreshOpponentTennisRecord()}>
                {opponentRefreshLoading ? 'Refreshing...' : 'Refresh Opponent EDR'}
              </Button>
              {form.opponent_tennis_record_roster_url.trim() && (
                <Button
                  variant="text"
                  href={form.opponent_tennis_record_roster_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open TennisRecord
                </Button>
              )}
              {form.opponent_edr_overall && (
                <Chip
                  size="small"
                  color={form.opponent_edr_source === 'tennis_record' ? 'success' : 'default'}
                  label={`TennisRecord Avg ${formatEdr(form.opponent_edr_overall)}`}
                />
              )}
            </Stack>
            <Typography variant="subtitle2" sx={{ gridColumn: '1 / -1', fontWeight: 800, mt: 1 }}>Opposing Captain Contacts</Typography>
            <Box sx={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
              <TextField
                fullWidth
                label="Opposing Captain Name"
                value={form.opponent_captain_names}
                onChange={(event) => updateForm({ opponent_captain_names: event.target.value })}
              />
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Opposing Captain Email"
                  value={form.opponent_captain_email}
                  onChange={(event) => updateForm({ opponent_captain_email: event.target.value })}
                  error={Boolean(form.opponent_captain_email && !isValidEmailAddress(form.opponent_captain_email))}
                  helperText={form.opponent_captain_email && !isValidEmailAddress(form.opponent_captain_email) ? 'Enter a valid email address.' : ''}
                />
                <TextField
                  fullWidth
                  label="Opposing Captain Phone"
                  type="tel"
                  value={form.opponent_captain_phone}
                  onChange={(event) => updateForm({ opponent_captain_phone: event.target.value })}
                />
              </Stack>
            </Box>
            <Box sx={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'start', mt: 2 }}>
              <TextField
                fullWidth
                label="Opposing Co-Captain Name"
                value={form.opponent_co_captain_name}
                onChange={(event) => updateForm({ opponent_co_captain_name: event.target.value })}
              />
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Opposing Co-Captain Email"
                  value={form.opponent_co_captain_email}
                  onChange={(event) => updateForm({ opponent_co_captain_email: event.target.value })}
                  error={Boolean(form.opponent_co_captain_email && !isValidEmailAddress(form.opponent_co_captain_email))}
                  helperText={form.opponent_co_captain_email && !isValidEmailAddress(form.opponent_co_captain_email) ? 'Enter a valid email address.' : ''}
                />
                <TextField
                  fullWidth
                  label="Opposing Co-Captain Phone"
                  type="tel"
                  value={form.opponent_co_captain_phone}
                  onChange={(event) => updateForm({ opponent_co_captain_phone: event.target.value })}
                />
              </Stack>
            </Box>
            <Typography variant="subtitle2" sx={{ gridColumn: '1 / -1', fontWeight: 800, mt: 1 }}>Status & Notes</Typography>
            <FormControl fullWidth required>
              <InputLabel>Match Status</InputLabel>
              <Select label="Match Status" value={form.match_status} onChange={(event) => updateForm({ match_status: event.target.value as MatchStatus })}>
                {matchStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value} disabled={option.value === 'Scores Entered' || option.value === 'Completed'}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {editing && (
              <>
                <TextField label="Lineup Status" value={optionLabel(lineupStatusOptions, editing.lineup_status || 'Not Started')} disabled />
                <TextField label="Lineup Published At" value={editing.lineup_published_at || ''} disabled />
                <TextField label="Lineup Published By" value={lookupLabel(lookups, 'app_users', editing.lineup_published_by)} disabled />
              </>
            )}
            <TextField
              label="Match Notes"
              value={form.notes_summary}
              multiline
              minRows={3}
              placeholder="Optional captain reference notes"
              sx={{ gridColumn: '1 / -1' }}
              onChange={(event) => updateForm({ notes_summary: event.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              if (!editing) onCreateMatchCancelled?.();
            }}
          >
            Cancel
          </Button>
          {!readOnly && (
            <Button variant="contained" onClick={() => void saveMatch()}>
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={bulkEditorOpen} onClose={cancelBulkEditor} maxWidth="xl" fullWidth>
        <DialogTitle>Bulk Edit Matches</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Alert severity="info">
              Editing {bulkEditableMatches.length} visible writable match{bulkEditableMatches.length === 1 ? '' : 'es'}.
            </Alert>
            {bulkEditorSaving && <LinearProgress />}
            {bulkEditableMatches.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No writable matches are available in the current filters.</Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '65vh' }}>
                <Table size="small" stickyHeader sx={{ minWidth: 2100 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, minWidth: 190 }}>Team</TableCell>
                      {matchBulkFields.map((field) => (
                        <TableCell key={field} sx={{ fontWeight: 800 }}>{matchBulkLabels[field]}</TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 800, minWidth: 90 }}>Changes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkEditableMatches.map((match) => {
                      const changedCells = matchBulkFields.filter((field) => isBulkCellChanged(match, field)).length;
                      return (
                        <TableRow key={match.id} sx={{ bgcolor: changedCells > 0 ? 'action.hover' : undefined }}>
                          <TableCell sx={{ fontWeight: 700, verticalAlign: 'top' }}>
                            {lookupLabel(lookups, 'teams', match.team_id)}
                            <Typography variant="caption" color="text.secondary" component="div">
                              {lookupLabel(lookups, 'seasons', match.season_id)}
                            </Typography>
                          </TableCell>
                          {matchBulkFields.map((field) => (
                            <TableCell key={field} sx={{ verticalAlign: 'top' }}>
                              {renderBulkEditorCell(match, field)}
                            </TableCell>
                          ))}
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            {changedCells > 0 ? <Chip size="small" color="warning" label={`${changedCells} changed`} /> : <Chip size="small" variant="outlined" label="Clean" />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelBulkEditor} disabled={bulkEditorSaving}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={() => void saveBulkEditor()} disabled={bulkEditorSaving || bulkEditableMatches.length === 0}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <BulkMatchImport
        open={bulkImportOpen}
        teams={writableTeams}
        facilities={lookups.facilities}
        existingMatches={matches}
        auditUserId={auditUserId}
        onClose={() => setBulkImportOpen(false)}
        onImported={loadMatches}
      />

      <Dialog open={Boolean(manualCopyMessage)} onClose={() => setManualCopyMessage('')} maxWidth="md" fullWidth>
        <DialogTitle>Copy Text</DialogTitle>
        <DialogContent>
          <TextField
            value={manualCopyMessage}
            multiline
            minRows={10}
            fullWidth
            margin="dense"
            inputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualCopyMessage('')}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
        {value || '-'}
      </Typography>
    </Box>
  );
}
